import React, { useState, useEffect, useMemo } from 'react';
import { createVehiclesForPerson, describeVehicleFailures, type VehicleCreateFailure } from '../../../services/personVehicles';
import { useCustomer } from '../../../contexts/CustomerContext';
import { buildLocationInfoRows } from './locationInfoRows';
import { DepartmentReportScope, buildDepartmentReportSections } from '../../../pages/departmentReport';
import { relationTypeLabel, personRelationLabel } from '../../../utils/relationType';
import { exportDepartmentReportPDF, exportDepartmentReportCSV } from '../../../pages/departmentReportExport';
import { LiberarAcessoModal } from '../../liberacoes/LiberarAcessoModal';
import { summarizeLiberacoes, LiberacaoSummary } from '../../../utils/liberacoes';
import { LiberadosHistoricoPanel } from './LiberadosHistoricoPanel';
import { toUnitHistorico } from '../../units/unitLiberados';
import { liberadosEndpoint } from '../../../pages/liberadosQuery';
import { ArrowLeft, Building2, Briefcase, Users, Calendar, Clock, User, Mail, Phone, Loader2, Home, UserPlus, RefreshCw, ShieldOff, Plus, History, UserCheck, Package, PawPrint, FileText, FileImage, Download, Upload, Trash2, ChevronRight, Search, Info } from 'lucide-react';
import { LicensePlate } from '../../ui/LicensePlate';
import { DateInput } from '../../ui/DateInput';
import { ReportButton } from '../../ui/ReportButton';
import { usePorteiroReports } from '../../../hooks/usePorteiroReports';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { tabCountLabel } from '../utils/tabCount';
import { cn } from '../../../utils/cn';
import { CondoType, getUnitLabel, getUnitArticle } from '../../../utils/terminology';
import { petSpeciesLabel, petBreedLabel } from '../../../utils/petLabels';
import { fetchWithAuth, condoApi, Pet, UnitDocument } from '../../../services/api';
import api from '../../../services/api';
import { DeviceInfo, DeviceOperations, CommunicationMode } from '../../../services/deviceCommunication';
import { DeviceIntegration } from '../../../integrations/devices';
import { syncVehicleRfid } from '../../../utils/vehicleRfidSync';
import toast from 'react-hot-toast';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { departmentFieldDisplayRows } from '../../../utils/departmentFields';
import { VehicleFormModal } from './VehicleFormModal';
import { UnitFormModal } from './UnitFormModal';
import { getParentescoLabel } from '../../registration/ParentescoField';
import { VehicleDetailModal } from './VehicleDetailModal';
import PetDetailModal from '../../PetDetailModal';
import VisitorRegistrationModal from '../../VisitorRegistrationModal';
import EmployeeRegistrationModal from '../../EmployeeRegistrationModal';
import customerService from '../../../services/customerService';
import { whatsappUrl, formatPhone } from '../../../utils/phone';
import { listEncomendas, Encomenda, ENCOMENDA_STATUS_LABELS, ENCOMENDA_STATUS_COLORS, ENCOMENDA_STATUS_COLORS_LIGHT } from '../../../services/encomendaApi';
import EncomendaDetailModal from './EncomendaDetailModal';
import EncomendaFormModal from './EncomendaFormModal';
import { isPrestadorVisit, liberadosForLocation, liberadosPrestadores, liberadosVisitantes } from './unitLiberadosRows';
import { PrestadorRow, splitUnitVisitRows, VisitRow } from './unitVisitRows';
import { useForceUppercase } from '../../../hooks/useForceUppercase';
import { appendIdentityPhoto } from '../../../services/identityPhotoUpload';
import { enrichResidentsWithPhotos } from '../utils/residentPhotos';
import { MinorReleasesPanel } from '../../MinorReleasesPanel';
import { foreignerPayload } from '../../../utils/foreigner';

interface LocationDetailViewProps {
  locationId: string;
  locationType: 'unit' | 'department' | 'block';
  customerId: string;
  customerType: 'condominio' | 'empresa';
  mobileAppEnabled?: boolean;
  condoType?: CondoType;
  petsEnabled?: boolean;
  funcionariosEnabled?: boolean;
  lightTheme?: boolean;
  externalRefreshTrigger?: number;
  onBack: () => void;
  onPersonClick?: (personId: string) => void;
  onUnitClick?: (unitId: string, unitName?: string) => void;
  onAddResident?: (unitId: string, blockId: string) => void;
  onAddVisitor?: (unitId: string, blockId: string) => void;
}

interface LocationInfo {
  id: string;
  name: string;
  description?: string;
  block_name?: string;
  street_name?: string;
  street_number?: string;
  manager_name?: string;
  units_count?: number;
  floors?: number;
  created_at?: string;
  updated_at?: string;
  /** Departamento only: the extra fields this empresa declared, keyed by field id. */
  custom_fields?: Record<string, string>;
}

interface PersonInLocation {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  profile_picture_url?: string;
  identity_photo_url?: string;
  identity_photos?: { photo_url: string }[];
  role_type?: string;
}

interface UnitInBlock {
  id: string;
  name: string;
  unit_number?: string;
  street_name?: string;
  street_number?: string;
}

// The row shapes live with the mapping that builds them, so the two cannot drift — see
// unitVisitRows.
type VisitRecord = VisitRow;
type SPRecord = PrestadorRow;

// Parse dd/mm/aaaa → YYYY-MM-DD (returns '' if invalid)
const parseBRDate = (s: string): string => {
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
};

// Mask dd/mm/aaaa as user types
const maskBRDate = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const BRDateInput: React.FC<{ value: string; onChange: (iso: string) => void; accentColor?: string }> = ({ value, onChange, accentColor = 'blue' }) => {
  const [text, setText] = React.useState(() => {
    if (!value) return '';
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  });
  React.useEffect(() => {
    if (!value) { setText(''); return; }
    const [y, m, d] = value.split('-');
    const br = `${d}/${m}/${y}`;
    setText(br);
  }, [value]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskBRDate(e.target.value);
    setText(masked);
    const iso = parseBRDate(masked);
    onChange(iso);
  };
  return (
    <input
      type="text"
      value={text}
      onChange={handleChange}
      placeholder="dd/mm/aaaa"
      maxLength={10}
      className={`flex-1 bg-gray-900 border border-gray-700 hover:border-gray-600 focus:border-${accentColor}-500 text-gray-200 text-xs rounded-lg px-2.5 py-1.5 focus:outline-none transition-colors placeholder-gray-600`}
    />
  );
};

const fmtDT = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const stateLabel = (state: string) => {
  switch (state) {
    case 'inside':         return 'Dentro';
    case 'awaiting_entry': return 'Aguardando';
    case 'exited':
    case 'consumed':       return 'Saiu';
    case 'expired':        return 'Expirou';
    case 'revoked':        return 'Revogado';
    default:               return state;
  }
};

const exportUnitPDF = async (
  type: 'visits' | 'prestadores',
  customerId: string,
  locationId: string,
  locationType: string,
  locationName: string,
  blockName?: string,
) => {
  // Fetch ALL records — two passes to get both active (Dentro/Aguardando) and inactive (Saiu/Expirou)
  //
  // Both role types every time, then split with isPrestadorVisit: asking the server for
  // `role_type=service_provider` alone left the Prestadores PDF missing every prestador registered
  // as a `visitor` role with role_metadata.category, which is most of them — and printed them on
  // the Visitas PDF instead. The paper now matches the tab it was exported from.
  const wantPrestadores = type === 'prestadores';
  const locationParam = locationType === 'unit' ? `unit_id=${locationId}` : `department_id=${locationId}`;
  let records: (VisitRecord | SPRecord)[] = [];

  const processResponse = (people: any[]) => {
    for (const person of people) {
      const name = `${person.first_name || ''} ${person.last_name || ''}`.trim() || '?';
      const photo = person.identity_photo_url || person.profile_picture_url || null;
      for (const role of (person.roles || [])) {
        if (role.role_type !== 'visitor' && role.role_type !== 'service_provider') continue;
        if (isPrestadorVisit(role) !== wantPrestadores) continue;
        const meta = role.role_metadata || {};
        if (locationType === 'unit' && meta.unit_id && String(meta.unit_id) !== String(locationId)) continue;
        // For empresa visits, department_id is stored as unit_id (legacy) or department_id.
        // Allow through if neither is set (old data without location info).
        if (locationType === 'department') {
          const visitDeptId = meta.department_id || meta.unit_id;
          if (visitDeptId && String(visitDeptId) !== String(locationId)) continue;
        }
        // skip duplicates already collected
        if (records.some(r => r.id === role.id)) continue;
        if (type === 'visits') {
          records.push({
            id: role.id, personId: person.id, name, photo,
            state: meta.visitor_state || 'unknown',
            createdAt: role.created_at,
            firstEntryAt: meta.first_entry_at,
            lastEntryAt: meta.last_entry_at,
            lastExitAt: meta.last_exit_at,
            authorizedByName: meta.authorized_by_name,
            liberacao: summarizeLiberacoes([role])[0],
          } as VisitRecord);
        } else {
          records.push({
            id: role.id, personId: person.id, name, photo,
            state: meta.visitor_state || 'unknown',
            createdAt: role.created_at,
            firstEntryAt: meta.first_entry_at,
            lastEntryAt: meta.last_entry_at,
            lastExitAt: meta.last_exit_at,
            revokedAt: meta.revoked_at,
            updatedAt: role.updated_at,
            liberacao: summarizeLiberacoes([role])[0],
          } as SPRecord);
        }
      }
    }
  };

  try {
    // Pass 1: active roles (Dentro, Aguardando)
    const r1 = await fetchWithAuth(
      `/api/v1/customers/${customerId}/people?role_type=visitor,service_provider&${locationParam}&page_size=1000`
    );
    if (r1.ok) {
      const d1 = await r1.json();
      processResponse(d1.data || d1 || []);
    }
    // Pass 2: inactive roles (Saiu, Expirou, Revogado)
    const r2 = await fetchWithAuth(
      `/api/v1/customers/${customerId}/people?role_type=visitor,service_provider&${locationParam}&include_inactive_roles=true&page_size=1000`
    );
    if (r2.ok) {
      const d2 = await r2.json();
      processResponse(d2.data || d2 || []);
    }
  } catch (e) {
    console.error('PDF fetch failed', e);
  }

  records.sort((a, b) =>
    new Date((b as any).firstEntryAt || (b as any).lastEntryAt || (b as any).lastExitAt || b.createdAt || 0).getTime() -
    new Date((a as any).firstEntryAt || (a as any).lastEntryAt || (a as any).lastExitAt || a.createdAt || 0).getTime()
  );

  const doc = new jsPDF('p', 'mm', 'a4');

  // Logo
  try {
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.src = '/panther-logo.png';
      img.onload = () => { try { doc.addImage(img, 'PNG', 14, 10, 18, 18); } catch {} resolve(); };
      img.onerror = () => resolve();
      setTimeout(resolve, 2000);
    });
  } catch {}

  // Header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('PANTHER ACCESS', 36, 18);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(type === 'visits' ? 'Relatório de Visitas' : 'Relatório de Prestadores', 36, 24);
  doc.setTextColor(0);

  doc.setDrawColor(220);
  doc.line(14, 30, 196, 30);

  // Unit info
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(locationName, 14, 38);
  if (blockName) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Bloco: ${blockName}`, 14, 44);
    doc.setTextColor(0);
  }
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}  ·  Total: ${records.length}`, 14, blockName ? 50 : 44);
  doc.setTextColor(0);

  const startY = blockName ? 56 : 50;

  if (type === 'visits') {
    autoTable(doc, {
      head: [['Nome', 'Estado', 'Aut. por', 'Entrada', 'Saída']],
      body: (records as VisitRecord[]).map(r => [
        r.name,
        stateLabel(r.state),
        r.authorizedByName || '-',
        fmtDT(r.firstEntryAt),
        fmtDT(r.lastExitAt),
      ]),
      startY,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 22 }, 2: { cellWidth: 45 }, 3: { cellWidth: 35 }, 4: { cellWidth: 35 } },
      margin: { left: 14, right: 14 },
      didDrawPage: (data) => {
        doc.setFontSize(7);
        doc.setTextColor(160);
        doc.text(`Página ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 8, { align: 'center' });
        doc.setTextColor(0);
      },
    });
  } else {
    autoTable(doc, {
      head: [['Nome', 'Estado', 'Entrada', 'Saída']],
      body: (records as SPRecord[]).map(r => {
        let exitCell: string;
        if (r.lastExitAt) {
          exitCell = fmtDT(r.lastExitAt);
        } else if (r.state === 'revoked') {
          exitCell = `Revogado: ${fmtDT(r.revokedAt || r.updatedAt)}`;
        } else if ((r.state === 'exited' || r.state === 'consumed') && r.updatedAt) {
          exitCell = `Saída manual: ${fmtDT(r.updatedAt)}`;
        } else if (r.state === 'exited' || r.state === 'consumed') {
          exitCell = 'Saída não registrada';
        } else {
          exitCell = '-';
        }
        return [r.name, stateLabel(r.state), fmtDT(r.firstEntryAt), exitCell];
      }),
      startY,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [194, 65, 12], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 25 }, 2: { cellWidth: 45 }, 3: { cellWidth: 45 } },
      margin: { left: 14, right: 14 },
      didDrawPage: (data) => {
        doc.setFontSize(7);
        doc.setTextColor(160);
        doc.text(`Página ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 8, { align: 'center' });
        doc.setTextColor(0);
      },
    });
  }

  const label = type === 'visits' ? 'visitas' : 'prestadores';
  const unitSlug = locationName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  doc.save(`${label}_${unitSlug}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

const ROLE_ORDER: Record<string, number> = { proprietario: 0, dependente: 1, familiar: 2 };

const sortByRole = (list: any[]): any[] =>
  [...list].sort((a, b) => {
    const ra = ROLE_ORDER[a.relation_type ?? ''] ?? 3;
    const rb = ROLE_ORDER[b.relation_type ?? ''] ?? 3;
    if (ra !== rb) return ra - rb;
    return `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(
      `${b.first_name || ''} ${b.last_name || ''}`, 'pt-BR'
    );
  });

export const LocationDetailView: React.FC<LocationDetailViewProps> = ({
  locationId,
  locationType,
  customerId,
  customerType,
  mobileAppEnabled,
  condoType,
  petsEnabled,
  funcionariosEnabled,
  lightTheme = false,
  onBack,
  onPersonClick,
  onUnitClick,
  onAddResident,
  onAddVisitor,
  externalRefreshTrigger,
}) => {
  const forceUpper = useForceUppercase();
  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
  const [people, setPeople] = useState<PersonInLocation[]>([]);
  const [units, setUnits] = useState<UnitInBlock[]>([]);
  const [unitSearch, setUnitSearch] = useState('');
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [isLoadingInfo, setIsLoadingInfo] = useState(true);
  const [isLoadingPeople, setIsLoadingPeople] = useState(true);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [blockId, setBlockId] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any | null>(null);
  const [vehicleToEdit, setVehicleToEdit] = useState<any | null>(null);
  const [syncingVehicleId, setSyncingVehicleId] = useState<string | null>(null);
  const [revokingVehicleId, setRevokingVehicleId] = useState<string | null>(null);
  const [authorizeVisitResident, setAuthorizeVisitResident] = useState<PersonInLocation | null>(null);
  // Department-level modals
  const [showDeptNewVisitModal, setShowDeptNewVisitModal] = useState(false);
  const [showDeptAuthorizeModal, setShowDeptAuthorizeModal] = useState(false);
  const [showDeptEmployeeModal, setShowDeptEmployeeModal] = useState(false);
  const [isSubmittingEmployee, setIsSubmittingEmployee] = useState(false);
  const [allDepts, setAllDepts] = useState<{ id: string; name: string }[]>([]);
  // Visit history
  const [activeTab, setActiveTab] = useState<'residents' | 'visits' | 'prestadores' | 'encomendas' | 'eventos'>('residents');
  const [visits, setVisits] = useState<VisitRecord[]>([]);
  const [spVisits, setSpVisits] = useState<SPRecord[]>([]);
  const [spFilter, setSpFilter] = useState<string>('');
  const [spPage, setSpPage] = useState(1);
  const [isLoadingVisits, setIsLoadingVisits] = useState(false);
  const [visitPersonFilter, setVisitPersonFilter] = useState<string>('');
  const [visitStateFilter, setVisitStateFilter] = useState<string>('');
  const [visitDateFrom, setVisitDateFrom] = useState<string>('');
  const [visitDateTo, setVisitDateTo] = useState<string>('');
  const [spDateFrom, setSpDateFrom] = useState<string>('');
  const [spDateTo, setSpDateTo] = useState<string>('');
  const [visitPage, setVisitPage] = useState(1);
  const PAGE_SIZE = 20;
  const [peoplePage, setPeoplePage] = useState(1);
  const PEOPLE_PAGE_SIZE = 20;
  const [deptSearch, setDeptSearch] = useState('');
  // Encomendas
  const [encomendas, setEncomendas] = useState<Encomenda[]>([]);
  const [isLoadingEncomendas, setIsLoadingEncomendas] = useState(false);
  const [selectedEncomendaId, setSelectedEncomendaId] = useState<string | null>(null);
  const [showEncomendaForm, setShowEncomendaForm] = useState(false);
  // Pets
  const [pets, setPets] = useState<Pet[]>([]);
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);

  // Funcionários (condo employees) assigned to this unit
  const [unitFuncionarios, setUnitFuncionarios] = useState<PersonInLocation[]>([]);

  // Unit Documents
  const [unitDocuments, setUnitDocuments] = useState<UnitDocument[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Event history tab
  const [eventHistory, setEventHistory] = useState<any[]>([]);
  const [loadingEventHistory, setLoadingEventHistory] = useState(false);
  const [eventHistoryError, setEventHistoryError] = useState<string | null>(null);
  const canViewReports = usePorteiroReports();
  const [eventHistorySearch, setEventHistorySearch] = useState('');
  const [eventHistoryDateFrom, setEventHistoryDateFrom] = useState(() => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [eventHistoryDateTo, setEventHistoryDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Refresh when parent signals (e.g. after visitor registration)
  useEffect(() => {
    if (externalRefreshTrigger && externalRefreshTrigger > 0) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [externalRefreshTrigger]);

  // Handle refresh button click
  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
    setTimeout(() => setIsRefreshing(false), 1000); // Reset animation after 1s
  };

  const fetchEventHistory = async (dateFrom: string, dateTo: string) => {
    const personIds = people.map(p => p.id).filter(Boolean);
    if (personIds.length === 0) { setEventHistory([]); setEventHistoryError(null); return; }
    setLoadingEventHistory(true);
    setEventHistoryError(null);
    try {
      // NOTE: intentionally NOT using /events/access's unit_id filter for units — this
      // codebase has two parallel unit-resident storage systems mid-migration (legacy
      // unit_residents table + newer person_roles.role_metadata), and until the backend
      // fix (event_service.go UnitID filter checking both) is deployed, unit_id silently
      // misses residents stored only in role_metadata. `people` here is already loaded
      // via the hybrid-aware /units/:id/residents endpoint, so person_ids is correct.
      const params = new URLSearchParams();
      params.set('customer_id', customerId);
      params.set('start_date', `${dateFrom}T00:00:00.000Z`);
      params.set('end_date', `${dateTo}T23:59:59.999Z`);
      params.set('limit', '200');
      personIds.forEach(id => params.append('person_ids', id));
      // NOT /api/v1/access-logs — that route is wired to a resident-self-service handler
      // that ignores person_ids and 403s for staff/admin callers. /events/access is the
      // real filter-respecting endpoint (same one the Monitoramento page uses).
      const resp = await fetchWithAuth(`/api/v1/events/access?${params.toString()}`);
      if (resp.ok) {
        const data = await resp.json();
        const events: any[] = data.data || data.events || data.logs || (Array.isArray(data) ? data : []);
        events.sort((a, b) => new Date(b.event_timestamp || b.processed_at || b.created_at || 0).getTime() - new Date(a.event_timestamp || a.processed_at || a.created_at || 0).getTime());
        setEventHistory(events);
        // Returned as well as stored: the report needs them NOW, and reading `eventHistory` right
        // after this would still see the previous render's value.
        return events;
      } else {
        const body = await resp.json().catch(() => ({}));
        setEventHistory([]);
        setEventHistoryError(
          resp.status === 403
            ? 'Sem permissão para ver o histórico de acesso'
            : `Erro ao carregar histórico${body.error ? `: ${body.error}` : ''}`
        );
      }
    } catch {
      setEventHistory([]);
      setEventHistoryError('Erro ao carregar histórico de acesso');
    }
    finally { setLoadingEventHistory(false); }
  };

  // Fetch event history when the eventos tab is activated
  useEffect(() => {
    if (activeTab === 'eventos' && (locationType === 'unit' || locationType === 'department') && !isLoadingPeople) {
      fetchEventHistory(eventHistoryDateFrom, eventHistoryDateTo);
    }
  }, [activeTab, people]);

  const handleCreateEmployee = async (submitData: { formData: any; photo: any | null; documentFrontPhoto?: any | null; documentBackPhoto?: any | null; vehicleList?: Array<{ plate: string; model: string; color: string }> }) => {
    const { formData: data, photo, documentFrontPhoto, documentBackPhoto, vehicleList } = submitData;
    setIsSubmittingEmployee(true);
    try {
      const response = await customerService.createEmployee(customerId, {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        cpf: data.cpf || undefined,
        position: data.position || undefined,
        department_ids: data.department_ids?.length ? data.department_ids : [locationId],
        department_id: locationId,
        is_minor: data.is_minor === true,
        ...foreignerPayload(data),
      });
      const employeeId = response.data?.id;
      if (photo && employeeId) {
        try {
          const fd = new FormData();
          appendIdentityPhoto(fd, photo);
          fd.append('customer_id', customerId);
          const photoResponse = await fetchWithAuth(`/api/v1/identity/staff/${employeeId}/photo`, { method: 'POST', body: fd });
          if (!photoResponse.ok) {
            toast.error('Funcionário criado, mas houve erro ao enviar a foto');
          }
        } catch {
          toast.error('Funcionário criado, mas houve erro ao enviar a foto');
        }
      }

      // Upload document photos (best-effort) — same generic /people/:id/photos endpoint
      // used for residents; employeeId returned above IS the person's id.
      // Declared out here because the vehicle warning is emitted alongside the success
      // message, below the `if (employeeId)` block the vehicles are created inside.
      let employeeVehicleFailures: VehicleCreateFailure[] = [];

      if (employeeId) {
        const docUpdates: Record<string, string> = {};
        for (const [key, phot, filename] of [
          ['document_front', documentFrontPhoto, 'document_front.jpg'],
          ['document_back',  documentBackPhoto,  'document_back.jpg'],
        ] as [string, any, string][]) {
          if (phot?.blob) {
            try {
              const fd = new FormData();
              fd.append('photo', phot.blob, filename);
              fd.append('customer_id', customerId);
              fd.append('photo_type', key);
              const r = await api.post(`/people/${employeeId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              const url = r.data?.data?.photo_url || r.data?.photo_url;
              if (url) docUpdates[`${key}_url`] = url;
            } catch { /* best effort */ }
          }
        }
        if (Object.keys(docUpdates).length > 0) {
          try { await condoApi.updatePerson(customerId, employeeId, docUpdates); } catch { /* best effort */ }
        }

        // Non-fatal: the employee is already committed, so a rejected vehicle POST must not
        // fail this flow — but it is reported after the success toast instead of swallowed.
        employeeVehicleFailures = await createVehiclesForPerson(customerId, employeeId, 'employee', vehicleList);
      }

      toast.success('Funcionário criado com sucesso!');
      const employeeVehicleWarning = describeVehicleFailures(employeeVehicleFailures);
      if (employeeVehicleWarning) toast.error(employeeVehicleWarning);
      setShowDeptEmployeeModal(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Erro ao criar funcionário');
    } finally {
      setIsSubmittingEmployee(false);
    }
  };

  // Fetch location details
  useEffect(() => {
    const fetchLocationInfo = async () => {
      setIsLoadingInfo(true);
      try {
        if (locationType === 'department') {
          const response = await fetchWithAuth(`/api/v1/customers/${customerId}/departments`);
          if (response.ok) {
            const data = await response.json();
            const departments = data.data || data.departments || data || [];
            setAllDepts(departments.map((d: any) => ({ id: d.id, name: d.name })));
            const dept = departments.find((d: any) => d.id === locationId);
            if (dept) {
              setLocationInfo({
                id: dept.id,
                name: dept.name,
                description: dept.description,
                manager_name: dept.manager_name,
                created_at: dept.created_at,
                updated_at: dept.updated_at,
                // The extra fields this empresa declared (CNPJ, nome fantasia, …) — the
                // porteiro has the company on the intercom and needs them on screen.
                custom_fields: dept.custom_fields,
              });
            }
          }
        } else if (locationType === 'block') {
          // For blocks, fetch block info and count units from global units endpoint
          const blocksResponse = await fetchWithAuth(`/api/v1/customers/${customerId}/blocks`);
          if (blocksResponse.ok) {
            const blocksData = await blocksResponse.json();
            const blocks = Array.isArray(blocksData) ? blocksData : (blocksData?.data || []);
            const block = blocks.find((b: any) => b.id === locationId);
            if (block) {
              // Fetch ALL units once and filter by block (much more efficient)
              const unitsResponse = await fetchWithAuth(`/api/v1/customers/${customerId}/units`);
              let unitsCount = 0;
              if (unitsResponse.ok) {
                const unitsData = await unitsResponse.json();
                const allUnits = Array.isArray(unitsData) ? unitsData : (unitsData?.data || []);
                // Count units for this specific block
                unitsCount = allUnits.filter((u: any) => u.block_id === block.id).length;
              }
              setLocationInfo({
                id: block.id,
                name: block.name,
                description: block.description,
                units_count: unitsCount,
                floors: block.floors,
                created_at: block.created_at,
                updated_at: block.updated_at,
              });
            }
          }
        } else {
          // For units, fetch ALL units once and find the one we need (much more efficient)
          const unitsResponse = await fetchWithAuth(`/api/v1/customers/${customerId}/units`);
          if (unitsResponse.ok) {
            const unitsData = await unitsResponse.json();
            const allUnits = Array.isArray(unitsData) ? unitsData : (unitsData?.data || []);

            // Helper to check if value is a UUID
            const isUUID = (val: string) => {
              return val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
            };

            // Find the unit directly from all units
            const unit = allUnits.find((u: any) => u.id === locationId);
            if (unit) {
              console.log('Unit detail data:', unit);

              // Determine display name with same logic as list
              const unitNumber = unit.unit_number || unit.number || unit.identifier;
              const unitName = unit.name;
              let displayName: string;

              if (unitNumber && !isUUID(unitNumber)) {
                displayName = unitNumber;
              } else if (unitName && !isUUID(unitName)) {
                displayName = unitName;
              } else {
                displayName = unitNumber || 'N/A';
              }

              setLocationInfo({
                id: unit.id,
                name: displayName,
                block_name: unit.block?.name || 'N/A',
                street_name: unit.street?.name,
                street_number: unit.street_number,
                created_at: unit.created_at,
                updated_at: unit.updated_at,
              });
              setBlockId(unit.block_id || unit.block?.id || ''); // Store the block ID
            }
          }
        }
      } catch (error) {
        console.error('Error fetching location info:', error);
      } finally {
        setIsLoadingInfo(false);
      }
    };

    fetchLocationInfo();
  }, [locationId, locationType, customerId, refreshTrigger]);

  // Fetch units for blocks
  useEffect(() => {
    const fetchUnits = async () => {
      if (locationType !== 'block') {
        setUnits([]);
        return;
      }

      setIsLoadingUnits(true);
      try {
        // Fetch ALL units and filter by block_id (more efficient than per-block requests)
        const response = await fetchWithAuth(`/api/v1/customers/${customerId}/units`);
        if (response.ok) {
          const data = await response.json();
          const allUnits = Array.isArray(data) ? data : (data?.data || []);
          // Filter units for this specific block
          const unitsData = allUnits.filter((u: any) => u.block_id === locationId);
          console.log('Units API response:', unitsData);
          const mappedUnits = unitsData.map((u: any, index: number) => {
            // Try multiple fields for unit name/number
            const unitNumber = u.unit_number || u.number || u.identifier;
            const unitName = u.name;

            // Check if a value looks like a UUID (36 chars with dashes in specific positions)
            const isUUID = (val: string) => {
              return val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
            };

            let displayName: string;
            if (unitNumber && !isUUID(unitNumber)) {
              displayName = unitNumber;
            } else if (unitName && !isUUID(unitName)) {
              displayName = unitName;
            } else {
              displayName = String(index + 1);
            }

            return {
              id: u.id,
              name: displayName,
              unit_number: unitNumber && !isUUID(unitNumber) ? unitNumber : undefined,
              street_name: u.street?.name,
              street_number: u.street_number,
            };
          });

          // Sort units using natural/numeric sorting
          const sortedUnits = [...mappedUnits].sort((a, b) => {
            return a.name.localeCompare(b.name, undefined, { numeric: true });
          });

          setUnits(sortedUnits);
        }
      } catch (error) {
        console.error('Error fetching units:', error);
      } finally {
        setIsLoadingUnits(false);
      }
    };

    fetchUnits();
  }, [locationId, locationType, refreshTrigger]);

  // Fetch people in this location (for departments and units, not blocks)
  useEffect(() => {
    setPeoplePage(1);
  }, [locationId]);

  useEffect(() => {
    const fetchPeople = async () => {
      // Blocks show units, not people directly
      if (locationType === 'block') {
        setPeople([]);
        setIsLoadingPeople(false);
        return;
      }

      setIsLoadingPeople(true);
      try {
        if (locationType === 'department') {
          // For departments, filter server-side by department_id to avoid pagination truncation
          const response = await fetchWithAuth(
            `/api/v1/customers/${customerId}/employees?department_id=${locationId}&page_size=1000`
          );
          if (response.ok) {
            const data = await response.json();
            const employees = data.data || data.employees || data || [];
            setPeople(Array.isArray(employees) ? employees : []);
          }
        } else {
          // For units, use the direct residents endpoint (same as Units page)
          // This correctly returns ALL residents for the unit
          const directResponse = await fetchWithAuth(`/api/v1/units/${locationId}/residents`);
          if (directResponse.ok) {
            const directData = await directResponse.json();
            const foundPeople = directData.data || directData.residents || directData || [];

            // Residents the list endpoint returned no face for get one from /people/:id/full,
            // decided per person — see utils/residentPhotos for why sampling one resident broke
            // the whole tab, and why profile_picture_url does not count as having a photo.
            const peopleWithPhotos = await enrichResidentsWithPhotos(foundPeople, async (personId) => {
              const fullPersonResponse = await fetchWithAuth(`/api/v1/people/${personId}/full`);
              return fullPersonResponse.ok ? fullPersonResponse.json() : null;
            });
            setPeople(sortByRole(peopleWithPhotos));
          }
        }
      } catch (error) {
        console.error('Error fetching people:', error);
      } finally {
        setIsLoadingPeople(false);
      }
    };

    fetchPeople();
  }, [locationId, locationType, customerId, refreshTrigger]);

  // Fetch vehicles for this unit or department
  useEffect(() => {
    if (locationType !== 'unit' && locationType !== 'department') {
      setVehicles([]);
      return;
    }
    const fetchVehicles = async () => {
      setIsLoadingVehicles(true);
      try {
        const param = locationType === 'department' ? `department_id=${locationId}` : `unit_id=${locationId}`;
        const response = await fetchWithAuth(
          `/api/v1/customers/${customerId}/vehicles?${param}&page_size=200`
        );
        if (response.ok) {
          const data = await response.json();
          setVehicles(data.vehicles || data.data || []);
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      } finally {
        setIsLoadingVehicles(false);
      }
    };
    fetchVehicles();
  }, [locationId, locationType, customerId, refreshTrigger]);

  // Fetch encomendas for this unit
  useEffect(() => {
    if (locationType !== 'unit') { setEncomendas([]); return; }
    setIsLoadingEncomendas(true);
    listEncomendas(customerId, { unit_id: locationId })
      .then(r => setEncomendas(r.data || []))
      .catch(() => {})
      .finally(() => setIsLoadingEncomendas(false));
  }, [locationId, locationType, customerId, refreshTrigger]);

  // Fetch pets for this unit (gated on feature flag)
  useEffect(() => {
    if (locationType !== 'unit' || !petsEnabled) { setPets([]); return; }
    condoApi.listPets(customerId, locationId)
      .then(data => setPets(data?.pets ?? []))
      .catch(() => setPets([]));
  }, [locationId, locationType, customerId, petsEnabled, refreshTrigger]);

  // Fetch condo employees ("funcionários") assigned to this unit (gated on feature flag)
  useEffect(() => {
    if (locationType !== 'unit' || !funcionariosEnabled) { setUnitFuncionarios([]); return; }
    fetchWithAuth(`/api/v1/customers/${customerId}/people?role_type=condo_employee&unit_id=${locationId}&page_size=100`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setUnitFuncionarios(data?.data ?? []))
      .catch(() => setUnitFuncionarios([]));
  }, [locationId, locationType, customerId, funcionariosEnabled, refreshTrigger]);

  // Fetch documents for this unit
  useEffect(() => {
    if (locationType !== 'unit') { setUnitDocuments([]); return; }
    condoApi.listUnitDocuments(customerId, locationId)
      .then(data => setUnitDocuments(data?.documents ?? []))
      .catch(() => setUnitDocuments([]));
  }, [locationId, locationType, customerId, refreshTrigger]);

  // Fetch visit history for units and departments
  useEffect(() => {
    if (locationType !== 'unit' && locationType !== 'department') return;
    const fetchVisits = async () => {
      setIsLoadingVisits(true);
      try {
        const locationParam = locationType === 'unit'
          ? `unit_id=${locationId}`
          : `department_id=${locationId}`;
        const resp = await fetchWithAuth(
          `/api/v1/customers/${customerId}/people?role_type=visitor,service_provider&${locationParam}&include_inactive_roles=true&page_size=50`
        );
        if (!resp.ok) return;
        const data = await resp.json();
        // Which tab each role lands on, and what its row carries — unitVisitRows, where it can be
        // asserted. It was written out here, and that is how it came to disagree with the
        // liberados sections about what a prestador is.
        const { visits: records, spVisits: spRecords } = splitUnitVisitRows(
          data.data || data || [],
          { locationType, locationId },
        );
        setVisits(records);
        setSpVisits(spRecords);
      } catch (err) {
        console.error('Failed to fetch visit history:', err);
      } finally {
        setIsLoadingVisits(false);
      }
    };
    fetchVisits();
  }, [locationId, locationType, customerId, refreshTrigger]);

  const visitPersonNames = useMemo(
    () => Array.from(new Set(visits.map(v => v.name))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [visits]
  );

  const applyDateFilter = (date: string | undefined, from: string, to: string): boolean => {
    if (!from && !to) return true;
    if (!date) return false;
    const d = new Date(date).getTime();
    if (from && d < new Date(from).getTime()) return false;
    if (to && d > new Date(to + 'T23:59:59').getTime()) return false;
    return true;
  };

  const filteredPeople = useMemo(() => {
    if (!deptSearch) return people;
    const q = deptSearch.toLowerCase();
    return people.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q));
  }, [people, deptSearch]);

  const filteredVisits = useMemo(() => {
    let list = visits;
    const nameQ = (visitPersonFilter || deptSearch).toLowerCase();
    if (nameQ) {
      list = list.filter(v => v.name.toLowerCase().includes(nameQ));
    }
    if (visitStateFilter) {
      if (visitStateFilter === 'exited') {
        list = list.filter(v => v.state === 'exited' || v.state === 'consumed');
      } else {
        list = list.filter(v => v.state === visitStateFilter);
      }
    }
    if (visitDateFrom || visitDateTo) {
      list = list.filter(v => applyDateFilter(v.firstEntryAt || v.createdAt, visitDateFrom, visitDateTo));
    }
    return list;
  }, [visits, visitPersonFilter, deptSearch, visitStateFilter, visitDateFrom, visitDateTo]);

  const filteredSpVisits = useMemo(() => {
    let list = spVisits;
    const nameQ = (spFilter || deptSearch).toLowerCase();
    if (nameQ) {
      list = list.filter(sp => sp.name.toLowerCase().includes(nameQ));
    }
    if (spDateFrom || spDateTo) {
      list = list.filter(sp => applyDateFilter(sp.firstEntryAt || sp.createdAt, spDateFrom, spDateTo));
    }
    return list;
  }, [spVisits, spFilter, deptSearch, spDateFrom, spDateTo]);

  // Reset pages when filters change
  useEffect(() => { setVisitPage(1); }, [visitPersonFilter, deptSearch, visitStateFilter, visitDateFrom, visitDateTo, visits]);
  useEffect(() => { setSpPage(1); }, [spFilter, deptSearch, spDateFrom, spDateTo, spVisits]);
  useEffect(() => { setPeoplePage(1); }, [deptSearch]);

  const visitTotalPages = Math.ceil(filteredVisits.length / PAGE_SIZE);
  const paginatedVisits = filteredVisits.slice((visitPage - 1) * PAGE_SIZE, visitPage * PAGE_SIZE);

  const spTotalPages = Math.ceil(filteredSpVisits.length / PAGE_SIZE);
  const paginatedSpVisits = filteredSpVisits.slice((spPage - 1) * PAGE_SIZE, spPage * PAGE_SIZE);

  const getResidentRoleLabel = (roleType?: string): { label: string; color: string } | null => {
    switch (roleType) {
      case 'resident':      return { label: 'Morador',      color: 'bg-blue-500/20 text-blue-400' };
      case 'owner':         return { label: 'Proprietário',  color: 'bg-green-500/20 text-green-400' };
      case 'dependent':     return { label: 'Dependente',    color: 'bg-blue-500/20 text-blue-400' };
      case 'tenant':        return { label: 'Locatário',     color: 'bg-yellow-500/20 text-yellow-400' };
      case 'employee':      return { label: 'Funcionário',   color: 'bg-orange-500/20 text-orange-400' };
      default:              return null;
    }
  };

  const getVisitStateLabel = (state: string): { label: string; color: string } => {
    switch (state) {
      case 'inside':          return { label: 'Dentro',          color: lightTheme ? 'bg-green-100 text-green-700'  : 'bg-green-500/20 text-green-400' };
      case 'awaiting_entry':  return { label: 'Aguardando',      color: lightTheme ? 'bg-yellow-100 text-yellow-700' : 'bg-yellow-500/20 text-yellow-400' };
      case 'pre_registered':  return { label: 'Pré-Cadastrado',  color: lightTheme ? 'bg-purple-100 text-purple-700' : 'bg-purple-500/20 text-purple-400' };
      case 'exited':
      case 'consumed':       return { label: 'Saiu',       color: lightTheme ? 'bg-gray-100 text-gray-600'    : 'bg-gray-500/20 text-gray-400' };
      case 'expired':        return { label: 'Expirou',    color: lightTheme ? 'bg-orange-100 text-orange-700' : 'bg-orange-500/20 text-orange-400' };
      case 'revoked':        return { label: 'Revogado',   color: lightTheme ? 'bg-red-100 text-red-700'      : 'bg-red-500/20 text-red-400' };
      default:               return { label: state,        color: lightTheme ? 'bg-gray-100 text-gray-600'    : 'bg-gray-500/20 text-gray-400' };
    }
  };

  const formatDateTime = (dateStr?: string): string => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch { return '—'; }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const Icon = locationType === 'department' ? Briefcase : Building2;
  const locationLabel = locationType === 'department' ? 'Departamento' : locationType === 'block' ? 'Bloco' : 'Unidade';
  const peopleLabel = customerType === 'empresa' ? 'Funcionarios' : 'Moradores';
  // What this empresa declared its departamentos carry (CNPJ, nome fantasia, …). Read from the
  // customer context rather than threaded through props: every caller of this view would
  // otherwise have to pass it, and forgetting one is a frame that silently drops the fields.
  const { selectedCustomer: customerForFields } = useCustomer();
  const departmentFields = (customerForFields as any)?.empresa_data?.department_field_config?.custom_fields;
  const unitLabel = getUnitLabel(condoType);
  const listLabel = locationType === 'block' ? 'Unidades neste bloco' : `${peopleLabel} neste ${locationLabel.toLowerCase()}`;
  const listCount = locationType === 'block' ? units.length : people.length;
  const isLoadingList = locationType === 'block' ? isLoadingUnits : isLoadingPeople;

  // Only the unit frame loses its side card; blocos/departamentos still need theirs for the
  // "nova unidade" button and the descrição/responsável that live nowhere else.
  const hideSideInfo = locationType === 'unit' || locationType === 'department';

  // Liberados: the resident cleared them ahead of time for a period or a weekday grade. The
  // sempre-habilitados have their own pinned block above, so they are excluded here, and a visitor
  // already inside is a visit in progress, not a pending liberação.
  const [showLiberarModal, setShowLiberarModal] = useState(false);

  // Who USED to be cleared for this unit. A second query — the ended clearances include
  // deactivated roles, which the live list never returns — scoped to the unit on screen and
  // reloaded whenever that changes, so a previous unit's endings never linger.
  const [historicoPeople, setHistoricoPeople] = useState<any[]>([]);
  const [isLoadingHistorico, setIsLoadingHistorico] = useState(false);

  useEffect(() => {
    if (locationType !== 'unit' || !customerId || !locationId) {
      setHistoricoPeople([]);
      return;
    }
    let cancelled = false;
    setIsLoadingHistorico(true);
    (async () => {
      try {
        const response = await fetchWithAuth(
          liberadosEndpoint({ customerId, page: 1, pageSize: 100, unitId: locationId, mode: 'history' })
        );
        const body = response.ok ? await response.json() : null;
        if (!cancelled) setHistoricoPeople(body?.data ?? []);
      } catch {
        if (!cancelled) setHistoricoPeople([]);
      } finally {
        if (!cancelled) setIsLoadingHistorico(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId, locationId, locationType, refreshTrigger]);

  const liberadosHistorico = useMemo(
    () => toUnitHistorico(historicoPeople, locationType === 'unit' ? locationId : undefined),
    [historicoPeople, locationId, locationType],
  );
  // Split the same way the live lists are: the Visitas tab answers about visitantes and the
  // Prestadores tab about prestadores, so a history that ignored the split would answer the
  // other tab's question on both.
  const historicoVisitantes = useMemo(
    () => liberadosHistorico.filter((row) => !row.isServiceProvider),
    [liberadosHistorico],
  );
  const historicoPrestadores = useMemo(
    () => liberadosHistorico.filter((row) => row.isServiceProvider),
    [liberadosHistorico],
  );

  // ONE list, permanent clearances included — see unitLiberadosRows for the hole this closes.
  const allLiberados = liberadosForLocation(visits, spVisits);
  const liberadoVisits = liberadosVisitantes(allLiberados);

  const hasTabActions =
    (locationType === 'unit' && activeTab === 'encomendas') ||
    (!!blockId && activeTab === 'residents' && (!!onAddResident || !!onAddVisitor)) ||
    locationType === 'department';

  /**
   * Same report the Departamento page produces — same scopes, same sections, same file name — built
   * from what this frame already has loaded. See departmentReport.ts for the contents and
   * departmentReportExport.ts for the paper.
   *
   * "completo" needs the access history, which this frame loads lazily for the Eventos tab; it is
   * fetched on demand so the report never ships an empty Eventos section just because the operator
   * never opened that tab.
   */
  const handleDepartmentReport = async (scope: DepartmentReportScope, format: 'pdf' | 'csv') => {
    const departmentName = locationInfo?.name || 'Departamento';
    let events = eventHistory;
    if (scope === 'completo' && events.length === 0) {
      events = (await fetchEventHistory(eventHistoryDateFrom, eventHistoryDateTo)) || [];
    }

    const sections = buildDepartmentReportSections(scope, {
      members: people.map(p => ({
        first_name: p.first_name,
        last_name: p.last_name,
        // This frame's person rows carry no position/CPF — the report prints a dash rather than
        // pretending the column does not exist, so the two screens produce the same shape.
        position: undefined,
        cpf: undefined,
        is_active: true,
      })),
      serviceProviders: spVisits.map(sp => ({
        name: sp.name,
        status_today: sp.state,
        last_entry_at: sp.lastEntryAt,
      })),
      events,
    });

    if (format === 'pdf') {
      await exportDepartmentReportPDF(departmentName, scope, sections);
    } else {
      exportDepartmentReportCSV(departmentName, scope, sections);
    }
  };

  const visitsReportButton =
    (activeTab === 'visits' || activeTab === 'prestadores') && !isLoadingVisits && canViewReports ? (
      <ReportButton
        dark={!lightTheme}
        title="Exportar PDF"
        formats={[{
          key: 'pdf',
          label: 'Exportar PDF',
          onSelect: () => exportUnitPDF(
            activeTab as 'visits' | 'prestadores',
            customerId,
            locationId,
            locationType,
            locationInfo?.name || locationId,
            locationInfo?.block_name,
          ),
        }]}
      />
    ) : null;

  const liberadoSpVisits = liberadosPrestadores(allLiberados);

  const infoRows = buildLocationInfoRows({
    locationType,
    locationInfo,
    peopleCount: people.length,
    unitsCount: units.length,
    peopleLabel,
    unitLabel,
    formatDate,
    // What this empresa declared its departamentos carry (CNPJ, nome fantasia, …).
    departmentFields,
  });

  /**
   * The departamento's declared fields, ON SCREEN.
   *
   * They were only in the header's hover tooltip, which is where information goes to be missed:
   * the porteiro working this frame has the company on the intercom and needs its CNPJ visible.
   * Unfilled fields still appear ("Sem informação") — an absent row is indistinguishable from a
   * field the empresa never declared.
   */
  const departmentInfoStrip = locationType === 'department'
    ? departmentFieldDisplayRows(departmentFields, locationInfo?.custom_fields, { includeEmpty: true })
    : [];

  const infoTooltip = (
    <div className="relative group flex-shrink-0 self-center">
      <button
        type="button"
        className="p-1 rounded-md text-gray-600 hover:text-gray-300 hover:bg-gray-800 transition-colors"
        aria-label="Informações"
      >
        <Info className="h-4 w-4" />
      </button>
      <div className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden min-w-[13rem] rounded-lg border border-gray-700 bg-gray-950 p-2.5 shadow-xl group-hover:block group-focus-within:block">
        {infoRows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-4 py-0.5">
            <span className="text-[11px] text-gray-500 whitespace-nowrap">{row.label}</span>
            <span className="text-[11px] text-gray-200 text-right">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );


  /**
   * "Liberados" — people a resident cleared in advance for a period or a weekday grade. Rendered on
   * both VISITAS and PREST., because a prestador can be liberado exactly the same way and the
   * portaria needs the same three facts either way: the window, whether it is open right now, and
   * whether to pick up the interfone.
   *
   * Shown even when empty: "nenhum ... liberado" is an answer the operator can act on, while an
   * absent section is indistinguishable from one that failed to load.
   */
  const renderLiberados = (
    list: Array<{ id: string; personId: string; name: string; photo: string | null; liberacao?: LiberacaoSummary }>,
    emptyLabel: string,
    isLoading: boolean,
  ) => (
    <div className="mb-3 flex-shrink-0">
      <div className="flex items-center gap-2 mb-1.5">
        <h3 className={cn(
          "text-[11px] font-semibold uppercase tracking-wider",
          lightTheme ? "text-gray-500" : "text-gray-400",
        )}>
          Liberados
        </h3>
        {/* The COUNT carries the colour, not the label. An emerald heading over "nenhum
            visitante liberado" announced good news about an empty list. */}
        <span className={cn(
          "text-[11px] font-bold px-1.5 py-0.5 rounded-full leading-none",
          isLoading || list.length === 0
            ? (lightTheme ? "bg-gray-100 text-gray-500" : "bg-gray-800 text-gray-400")
            : (lightTheme ? "bg-emerald-100 text-emerald-700" : "bg-emerald-500/20 text-emerald-300"),
        )}>
          {isLoading ? '…' : list.length}
        </span>
        {locationType === 'unit' && (
          <button
            type="button"
            onClick={() => setShowLiberarModal(true)}
            className={cn(
              'ml-auto flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors',
              lightTheme
                ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                : 'border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30',
            )}
          >
            <Plus className="h-3 w-3" />
            Liberar
          </button>
        )}
      </div>

      {!isLoading && list.length === 0 ? (
        /* One quiet line. A full-width 36px green panel to report zero took as much of the
           frame as two real liberados would. */
        <p className={cn("text-[11.5px] px-0.5", lightTheme ? "text-gray-400" : "text-gray-600")}>
          {emptyLabel}
        </p>
      ) : (
        <div className="space-y-1">
          {list.map(v => (
            <button
              key={v.id}
              onClick={() => onPersonClick?.(v.personId)}
              className={cn(
                "flex items-center gap-2 w-full text-left rounded-lg px-2.5 py-1.5 border transition-colors",
                lightTheme
                  ? "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  : "bg-gray-950 border-gray-800 hover:border-gray-700 hover:bg-gray-900",
              )}
            >
              <div className={cn(
                "w-7 h-7 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center",
                lightTheme ? "bg-gray-100" : "bg-gray-800",
              )}>
                {v.photo
                  ? <img src={getAbsoluteImageUrl(v.photo)} alt={v.name} className="w-full h-full object-cover" />
                  : <span className={cn("text-[11px] font-semibold", lightTheme ? "text-gray-500" : "text-gray-400")}>{v.name[0]}</span>
                }
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span style={forceUpper ? { textTransform: 'uppercase' } : undefined} className={cn("text-xs font-semibold truncate", lightTheme ? "text-gray-900" : "text-gray-200")}>{v.name}</span>
                <span className={cn("text-[11px] truncate", lightTheme ? "text-gray-500" : "text-gray-400")}>
                  {[v.liberacao?.periodLabel, v.liberacao?.rangeLabel].filter(Boolean).join(' · ')}
                </span>
              </div>
              {v.liberacao?.notStartedYet && (
                <span className={cn(
                  "text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0",
                  lightTheme ? "bg-red-50 text-red-700 border-red-200" : "bg-red-500/15 text-red-400 border-red-500/30",
                )}>Ainda não válida</span>
              )}
              {!v.liberacao?.notStartedYet && v.liberacao?.withinWindowNow === false && (
                <span className={cn(
                  "text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0",
                  lightTheme ? "bg-red-50 text-red-700 border-red-200" : "bg-red-500/15 text-red-400 border-red-500/30",
                )}>Fora do horário</span>
              )}
              {!v.liberacao?.notStartedYet && v.liberacao?.withinWindowNow === true && (
                <span className={cn(
                  "text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0",
                  lightTheme ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
                )}>No horário</span>
              )}
              {/* The one thing that changes what the operator physically does next. */}
              <span className={cn(
                "text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0",
                v.liberacao?.requiresIntercom
                  ? (lightTheme ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-amber-500/15 text-amber-400 border-amber-500/30")
                  : (lightTheme ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-gray-800 text-gray-400 border-gray-700"),
              )}>
                {v.liberacao?.requiresIntercom ? 'Interfonar' : 'Sem interfone'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const handleVehicleSyncRevoke = async (vehicle: any, isAllowed: boolean) => {
    const setState = isAllowed ? setSyncingVehicleId : setRevokingVehicleId;
    setState(vehicle.id);
    try {
      await syncVehicleRfid({ customerId: customerId!, vehicle, isAllowed });
    } finally {
      setState(null);
    }
  };

  const handleDeleteVehicle = async (vehicle: any) => {
    if (!customerId) return;
    if (!window.confirm(`Deseja realmente excluir o veículo ${vehicle.plate}?`)) return;
    try {
      const res = await fetchWithAuth(`/api/v1/customers/${customerId}/vehicles/${vehicle.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Veículo excluído!');
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error('Erro ao excluir veículo');
      }
    } catch {
      toast.error('Erro ao excluir veículo');
    }
  };

  return (
    <div className="lg:col-span-3 2xl:col-span-4 bg-black rounded-xl border border-gray-800 px-4 pt-2 pb-3 flex flex-col overflow-hidden">
      {/* Header with back button — only for non-department locations */}
      {locationType !== 'department' && (
        <div className="flex items-center gap-2 mb-2 flex-shrink-0">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
            title="Voltar"
          >
            <ArrowLeft className="h-5 w-5 text-gray-400" />
          </button>
          {/* One line: "Bloco A › Unidade 14". The breadcrumb used to sit on a line of its own
              above the number, which bought nothing and cost a row of height on a screen where
              every row is a visitor the porteiro cannot see. */}
          <div className="flex items-baseline gap-1.5 flex-1 min-w-0">
            {locationInfo?.block_name && (
              <>
                <span className="text-xs font-medium text-gray-500 truncate">{locationInfo.block_name}</span>
                <ChevronRight className="h-3 w-3 text-gray-600 flex-shrink-0 self-center" />
              </>
            )}
            <span className="text-xs font-medium text-gray-400 flex-shrink-0">{locationLabel}</span>
            <h2 className="text-xl font-bold text-white truncate leading-tight">
              {isLoadingInfo ? 'Carregando...' : locationInfo?.name || locationLabel}
            </h2>
            {/* The street address, where the condo has one. Quadra + lote is the internal
                reference; this is what the visitor at the gate says out loud. It lived only
                inside the hover tooltip, which is no use while reading an intercom. */}
            {locationInfo?.street_name && (
              <span
                data-testid="location-header-address"
                className="text-xs font-medium text-gray-400 truncate"
                title={locationInfo.street_number ? `${locationInfo.street_name}, ${locationInfo.street_number}` : locationInfo.street_name}
              >
                · {locationInfo.street_number ? `${locationInfo.street_name}, ${locationInfo.street_number}` : locationInfo.street_name}
              </span>
            )}
            {/* Everything the old side card held, on hover, costing no layout. */}
            {infoTooltip}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              "p-2 rounded-lg transition-all",
              isRefreshing
                ? "text-blue-500"
                : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
            )}
            title="Atualizar"
          >
            <RefreshCw className={cn("h-5 w-5", isRefreshing && "animate-spin")} />
          </button>
        </div>
      )}

      {/* Main content. A unit gets the whole frame: its side card only repeated the breadcrumb and
          the tab counters, and it is now the header tooltip. Blocos and departamentos keep theirs —
          it carries the "nova unidade" action and the descrição/responsável. */}
      <div className={cn(
        'flex-1 grid grid-cols-1 gap-4 min-h-0 overflow-hidden',
        !hideSideInfo && 'lg:grid-cols-3',
      )}>
        {/* Left column - People or Units list.
            THE single scroll container for this frame. Each section used to scroll inside its own
            fixed-height box with the scrollbar hidden, so at a small viewport the moradores list,
            the veículos list and the documentos list each clipped a row in half with nothing on
            screen to say more existed — and a lembrete at the top pushed everything into those
            invisible wells at once. Now the whole column scrolls, once, with a visible bar. */}
        <div
          className={cn(
            'flex flex-col min-h-0 flex-1 overflow-y-auto custom-scrollbar pr-2',
            !hideSideInfo && 'lg:col-span-2',
          )}
        >

          {/* Tab bar — for units and departments. Sticky, not scrolled: it is the frame's
              navigation, and it used to disappear behind a long veículos list. */}
          {(locationType === 'unit' || locationType === 'department') ? (
            <div className="mb-3 flex-shrink-0 sticky top-0 z-20 bg-black pt-1">
            {/* Row 1: back button + name + ⓘ (fixed) then the tab buttons (scrollable).
                The identity segment sits OUTSIDE the scroller on purpose: an overflow-x-auto
                box clips an absolutely-positioned popup on both axes, so the ⓘ tooltip would
                be unreadable inside it. */}
            <div className="flex items-center gap-1 border-b border-gray-800 pb-1 min-w-0">
              {locationType === 'department' && (
                <>
                  <button
                    onClick={onBack}
                    className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0 mr-1"
                    title="Voltar"
                  >
                    <ArrowLeft className="h-4 w-4 text-gray-400" />
                  </button>
                  <span className="text-sm font-semibold text-gray-200 px-1 truncate max-w-[7rem] flex-shrink-0" title={locationInfo?.name}>
                    {isLoadingInfo ? '…' : locationInfo?.name || 'Departamento'}
                  </span>
                  {/* Everything the removed side card held — descrição, responsável, total,
                      criado/atualizado — on hover, costing no layout. The declared fields are
                      NOT in here: see the strip below, where the porteiro can read them without
                      hovering. */}
                  {infoTooltip}
                  <span className="text-gray-700 mx-1 flex-shrink-0">·</span>
                </>
              )}
              <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto scrollbar-none">
              <button
                onClick={() => setActiveTab('residents')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-medium uppercase tracking-wide transition-colors flex-shrink-0",
                  activeTab === 'residents'
                    ? "bg-blue-600 text-white"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                )}
              >
                {peopleLabel}
                <span className={cn(
                  "px-1.5 py-0.5 rounded-full text-xs",
                  activeTab === 'residents' ? "bg-blue-500 text-white" : "bg-gray-700 text-gray-300"
                )}>
                  {tabCountLabel({ isLoading: isLoadingPeople, total: people.length, filtered: filteredPeople.length, isSearching: !!deptSearch })}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('visits')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-medium uppercase tracking-wide transition-colors flex-shrink-0",
                  activeTab === 'visits'
                    ? "bg-blue-600 text-white"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                )}
              >
                VISITAS
                <span className={cn(
                  "px-1.5 py-0.5 rounded-full text-xs",
                  activeTab === 'visits' ? "bg-blue-500 text-white" : "bg-gray-700 text-gray-300"
                )}>
                  {tabCountLabel({ isLoading: isLoadingVisits, total: visits.length, filtered: filteredVisits.length, isSearching: !!deptSearch })}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('prestadores')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-medium uppercase tracking-wide transition-colors flex-shrink-0",
                  activeTab === 'prestadores'
                    ? "bg-orange-600 text-white"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                )}
              >
                PREST.
                <span className={cn(
                  "px-1.5 py-0.5 rounded-full text-xs",
                  activeTab === 'prestadores' ? "bg-orange-500 text-white" : "bg-gray-700 text-gray-300"
                )}>
                  {tabCountLabel({ isLoading: isLoadingVisits, total: spVisits.length, filtered: filteredSpVisits.length, isSearching: !!deptSearch })}
                </span>
              </button>
              {locationType === 'unit' && <button
                onClick={() => setActiveTab('encomendas')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-medium uppercase tracking-wide transition-colors flex-shrink-0",
                  activeTab === 'encomendas'
                    ? "bg-amber-600 text-white"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                )}
              >
                ENCOM.
                <span className={cn(
                  "px-1.5 py-0.5 rounded-full text-xs",
                  activeTab === 'encomendas' ? "bg-amber-500 text-white" : "bg-gray-700 text-gray-300"
                )}>
                  {tabCountLabel({ isLoading: isLoadingEncomendas, total: encomendas.length, filtered: encomendas.length, isSearching: false })}
                </span>
              </button>}
              <button
                onClick={() => setActiveTab('eventos')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-medium uppercase tracking-wide transition-colors flex-shrink-0",
                  activeTab === 'eventos'
                    ? "bg-blue-600 text-white"
                    : lightTheme
                      ? "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                )}
              >
                EVENTOS
                <span className={cn(
                  "px-1.5 py-0.5 rounded-full text-xs",
                  activeTab === 'eventos' ? "bg-blue-500 text-white" : lightTheme ? "bg-gray-200 text-gray-600" : "bg-gray-700 text-gray-300"
                )}>
                  {loadingEventHistory ? '…' : eventHistory.length}
                </span>
              </button>
              </div>
            </div>
            {/* The departamento's declared fields, on screen rather than behind a hover. */}
            {departmentInfoStrip.length > 0 && (
              <div
                data-testid="department-info-strip"
                className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-2 text-xs"
              >
                {departmentInfoStrip.map(row => (
                  <span key={row.id} className="whitespace-nowrap">
                    <span className="text-gray-500">{row.label}: </span>
                    <span className="text-gray-200">{row.value}</span>
                  </span>
                ))}
              </div>
            )}
            {/* Row 2: action buttons (right-aligned). Hidden when it has nothing to show — with
                Relatório moved into the filter row it is empty on a unit's VISITAS/PREST. tab, and
                an empty flex row still costs its padding. */}
            {hasTabActions && (
            <div className="flex items-center justify-end gap-1 pt-1 flex-wrap">
              {locationType === 'unit' && activeTab === 'encomendas' && (
                <button
                  onClick={() => setShowEncomendaForm(true)}
                  className={cn(
                    "flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors border",
                    lightTheme
                      ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                      : "border-transparent bg-amber-600 hover:bg-amber-700 text-white"
                  )}
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Registrar</span>
                </button>
              )}
              {blockId && activeTab === 'residents' && (
                <>
                  {onAddResident && (
                    <button
                      onClick={() => onAddResident(locationId, blockId)}
                      className={cn(
                        "flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors border",
                        lightTheme
                          ? "border-blue-300 text-blue-700 hover:bg-blue-50"
                          : "border-transparent bg-blue-600 hover:bg-blue-700 text-white"
                      )}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      <span>Novo Morador</span>
                    </button>
                  )}
                  {onAddVisitor && (
                    <button
                      onClick={() => onAddVisitor(locationId, blockId)}
                      className={cn(
                        "flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors border",
                        lightTheme
                          ? "border-blue-300 text-blue-700 hover:bg-blue-50"
                          : "border-transparent bg-blue-600 hover:bg-blue-700 text-white"
                      )}
                    >
                      <User className="h-3.5 w-3.5" />
                      <span>Novo Visitante</span>
                    </button>
                  )}
                </>
              )}
              {locationType === 'department' && activeTab === 'residents' && (
                <button
                  onClick={() => setShowDeptEmployeeModal(true)}
                  className={cn(
                    "flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors border",
                    lightTheme
                      ? "border-blue-300 text-blue-700 hover:bg-blue-50"
                      : "border-transparent bg-blue-600 hover:bg-blue-700 text-white"
                  )}
                >
                  <Briefcase className="h-3.5 w-3.5" />
                  <span>Novo Funcionário</span>
                </button>
              )}
              {locationType === 'department' && activeTab === 'visits' && (
                <>
                  <button
                    onClick={() => setShowDeptNewVisitModal(true)}
                    className={cn(
                      "flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors border",
                      lightTheme
                        ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        : "border-transparent bg-emerald-600 hover:bg-emerald-700 text-white"
                    )}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span>Nova Visita</span>
                  </button>
                  <button
                    onClick={() => setShowDeptAuthorizeModal(true)}
                    className={cn(
                      "flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors border",
                      lightTheme
                        ? "border-violet-300 text-violet-700 hover:bg-violet-50"
                        : "border-transparent bg-violet-600 hover:bg-violet-700 text-white"
                    )}
                  >
                    <UserCheck className="h-3.5 w-3.5" />
                    <span>Autorizar</span>
                  </button>
                </>
              )}
              {locationType === 'department' && canViewReports && (
                <ReportButton
                  dark={!lightTheme}
                  title="Relatório de funcionários"
                  // Funcionários only. Six entries wrapping onto three lines each was a menu you
                  // had to read rather than pick from; the other two scopes live on the
                  // Departamento page, which has room for them.
                  formats={[
                    { key: 'pdf', label: 'PDF', onSelect: () => handleDepartmentReport('funcionarios', 'pdf') },
                    { key: 'csv', label: 'CSV', onSelect: () => handleDepartmentReport('funcionarios', 'csv') },
                  ]}
                />
              )}
              {locationType === 'department' && (
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={cn(
                    "p-1.5 rounded-lg transition-all flex-shrink-0",
                    isRefreshing
                      ? "text-blue-500"
                      : lightTheme
                        ? "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                  )}
                  title="Atualizar"
                >
                  <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                </button>
              )}
            </div>
            )}
            {locationType === 'department' && activeTab === 'residents' && (
              <div className="relative mt-2">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar funcionários, visitas, prestadores..."
                  value={deptSearch}
                  onChange={e => setDeptSearch(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 hover:border-gray-600 focus:border-blue-500 text-gray-200 text-xs rounded-lg pl-8 pr-7 py-2 focus:outline-none transition-colors placeholder-gray-600"
                />
                {deptSearch && (
                  <button onClick={() => setDeptSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            )}
            </div>
          ) : (
            /* Non-unit header */
            locationType !== 'block' && (
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-300">
                <Users className="h-4 w-4 text-blue-500" />
                {listLabel}
              </h3>
              <span className="text-sm font-medium text-gray-500">
                {listCount}
              </span>
            </div>
            )
          )}

          {/* Termos de menores filed against THIS unit — shown with the residents, because it
              answers a question about them: which of this household's children may use the
              readers, and whether anything is waiting on a decision. */}
          {locationType === 'unit' && activeTab === 'residents' && (
            <div className="mb-3">
              <MinorReleasesPanel
                customerId={customerId}
                customerName=""
                enabled
                unitId={locationId}
                unitLabel={locationInfo?.name || ''}
              />
            </div>
          )}

          {/* Eventos tab content */}
          {(locationType === 'unit' || locationType === 'department') && activeTab === 'eventos' && (
            <div className="flex flex-col gap-2 mb-2">
              {/* Filters */}
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <svg className={cn("absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none", lightTheme ? "text-gray-400" : "text-gray-500")} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Buscar por pessoa..."
                    value={eventHistorySearch}
                    onChange={e => setEventHistorySearch(e.target.value)}
                    className={cn(
                      "w-full text-xs rounded-lg pl-8 pr-7 py-2 focus:outline-none transition-colors",
                      lightTheme
                        ? "bg-white border border-gray-300 hover:border-gray-400 focus:border-violet-400 text-gray-800 placeholder-gray-400"
                        : "bg-gray-900 border border-gray-700 hover:border-gray-600 focus:border-violet-500 text-gray-200 placeholder-gray-600"
                    )}
                  />
                  {eventHistorySearch && (
                    <button onClick={() => setEventHistorySearch('')} className={cn("absolute right-2 top-1/2 -translate-y-1/2 transition-colors", lightTheme ? "text-gray-400 hover:text-gray-600" : "text-gray-500 hover:text-gray-300")}>
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Calendar className={cn("h-3.5 w-3.5 flex-shrink-0", lightTheme ? "text-gray-400" : "text-gray-500")} />
                  <DateInput
                    value={`${eventHistoryDateFrom}T12:00:00`}
                    onChange={iso => setEventHistoryDateFrom(iso ? iso.split('T')[0] : eventHistoryDateFrom)}
                    className={cn(
                      "flex-1 w-full text-xs rounded-lg px-2 py-1.5 focus:outline-none",
                      lightTheme
                        ? "bg-white border border-gray-300 text-gray-800 focus:border-violet-400"
                        : "bg-gray-900 border border-gray-700 text-gray-200 focus:border-violet-500"
                    )}
                  />
                  <span className={cn("text-xs", lightTheme ? "text-gray-400" : "text-gray-600")}>–</span>
                  <DateInput
                    value={`${eventHistoryDateTo}T12:00:00`}
                    onChange={iso => setEventHistoryDateTo(iso ? iso.split('T')[0] : eventHistoryDateTo)}
                    className={cn(
                      "flex-1 w-full text-xs rounded-lg px-2 py-1.5 focus:outline-none",
                      lightTheme
                        ? "bg-white border border-gray-300 text-gray-800 focus:border-violet-400"
                        : "bg-gray-900 border border-gray-700 text-gray-200 focus:border-violet-500"
                    )}
                  />
                  <button
                    onClick={() => fetchEventHistory(eventHistoryDateFrom, eventHistoryDateTo)}
                    disabled={loadingEventHistory}
                    className={cn(
                      "px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50",
                      lightTheme
                        ? "text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200"
                        : "text-violet-300 bg-violet-900/30 hover:bg-violet-900/50 border border-violet-700/40"
                    )}
                  >
                    Filtrar
                  </button>
                </div>
              </div>
              {/* List */}
              {loadingEventHistory ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500" />
                </div>
              ) : (() => {
                const filtered = eventHistory.filter(e =>
                  !eventHistorySearch || (e.person_name || '').toLowerCase().includes(eventHistorySearch.toLowerCase())
                );
                if (filtered.length === 0) {
                  return (
                    <div className={cn("flex flex-col items-center justify-center py-8", eventHistoryError ? "text-red-500" : lightTheme ? "text-gray-400" : "text-gray-600")}>
                      <History className={cn("h-8 w-8 mb-2", eventHistoryError ? "text-red-300" : lightTheme ? "text-gray-300" : "text-gray-700")} />
                      <p className="text-xs">{eventHistoryError || 'Nenhum evento encontrado'}</p>
                    </div>
                  );
                }
                const getEventLabel = (ev: any): string => {
                  const evType = ev.event_type || '';
                  const dir = ev.direction || '';
                  const ok = ev.is_authorized !== false && ev.status !== 'failure';
                  if (evType === 'access_granted' || evType === 'authentication_success' || (dir === 'entrada' && ok)) return 'Entrada';
                  if (evType === 'access_denied' || evType === 'authentication_failure' || !ok) return 'Negado';
                  if (evType.includes('exit') || dir === 'saida') return 'Saída';
                  if (dir === 'entrada') return 'Entrada';
                  return evType.replace(/_/g, ' ') || '—';
                };
                const formatTs = (ev: any): string => {
                  const raw = ev.event_timestamp || ev.processed_at || ev.created_at;
                  if (!raw) return '—';
                  try { return new Date(raw).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
                };
                // identity_photo_url first, matching every other avatar here: profile_picture_url is
                // a mirror nothing repoints when the identity photo it names is replaced or deleted.
                const photoMap = new Map(people.map(p => [p.id, getAbsoluteImageUrl(p.identity_photo_url || p.profile_picture_url || p.identity_photos?.[0]?.photo_url || '')]));
                const borderColor = lightTheme ? 'border-white' : 'border-gray-950';
                return (
                  <div className="space-y-1.5">
                    {filtered.map((ev, i) => {
                      const label = getEventLabel(ev);
                      const isEntry = label === 'Entrada';
                      const isDenied = label === 'Negado';
                      const photoUrl = ev.person_id ? photoMap.get(ev.person_id) : undefined;
                      const initials = (ev.person_name || '').split(' ').filter(Boolean).map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
                      return (
                        <div key={ev.id || i} className={cn("flex items-center gap-3 rounded-lg px-3 py-2", lightTheme ? "bg-gray-50 border border-gray-200" : "bg-gray-950 border border-gray-800")}>
                          <div className="relative flex-shrink-0">
                            {photoUrl ? (
                              <img src={photoUrl} alt={ev.person_name || ''} className="w-6 h-8 rounded-md object-cover" />
                            ) : (
                              <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium", lightTheme ? "bg-gray-200 text-gray-500" : "bg-gray-800 text-gray-400")}>
                                {initials || <User className="h-3 w-3" />}
                              </div>
                            )}
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border ${borderColor} ${isEntry ? 'bg-green-500' : isDenied ? 'bg-red-500' : 'bg-gray-400'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {/* Clickable like every other person reference in this frame: an
                                  event row is usually where an operator first spots someone, and
                                  the name was the one dead end. Events with no person_id (an
                                  unrecognised face, a plate with no owner) stay plain text —
                                  there is nothing to open. */}
                              {ev.person_id && onPersonClick ? (
                                <button
                                  onClick={() => onPersonClick(ev.person_id)}
                                  style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                                  className={cn(
                                    "text-xs font-medium truncate text-left hover:underline",
                                    lightTheme ? "text-gray-800 hover:text-blue-600" : "text-gray-200 hover:text-blue-400",
                                  )}
                                  title="Abrir cadastro"
                                >
                                  {ev.person_name || 'Desconhecido'}
                                </button>
                              ) : (
                                <p style={forceUpper ? { textTransform: 'uppercase' } : undefined} className={cn("text-xs font-medium truncate", lightTheme ? "text-gray-800" : "text-gray-200")}>{ev.person_name || 'Desconhecido'}</p>
                              )}
                              <span className={cn(
                                "text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0",
                                isEntry
                                  ? (lightTheme ? 'bg-green-100 text-green-700' : 'bg-green-900/40 text-green-400')
                                  : isDenied
                                    ? (lightTheme ? 'bg-red-100 text-red-700' : 'bg-red-900/40 text-red-400')
                                    : (lightTheme ? 'bg-gray-100 text-gray-600' : 'bg-gray-800 text-gray-400')
                              )}>{label}</span>
                            </div>
                            <p className={cn("text-[10px] mt-0.5", lightTheme ? "text-gray-400" : "text-gray-500")}>{formatTs(ev)}{ev.device_name ? ` · ${ev.device_name}` : ''}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Prestadores tab content */}
          {(locationType === 'unit' || locationType === 'department') && activeTab === 'prestadores' ? (
            <div className="flex flex-col min-h-0">
              {renderLiberados(liberadoSpVisits, 'Nenhum prestador liberado', isLoadingVisits)}
              {/* The prestadores that USED to be cleared for this unit. The Prestadores tab had a
                  Liberados block and no history at all. */}
              {locationType === 'unit' && (
                <LiberadosHistoricoPanel
                  rows={historicoPrestadores}
                  isLoading={isLoadingHistorico}
                  onPersonClick={(personId) => onPersonClick?.(personId)}
                  lightTheme={lightTheme}
                  forceUpper={forceUpper}
                />
              )}

              {spVisits.length > 0 && (
                <div className="mb-3 flex-shrink-0 space-y-2">
                  <div className="flex gap-2">
                  <div className="relative flex-1">
                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                    <input type="text" placeholder="Buscar prestador..." value={spFilter} onChange={e => setSpFilter(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 hover:border-gray-600 focus:border-orange-500 text-gray-200 text-xs rounded-lg pl-8 pr-7 py-2 focus:outline-none transition-colors placeholder-gray-600"
                    />
                    {spFilter && (
                      <button onClick={() => setSpFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                  {/* Relatório lives in the filter row here too — it used to share a row of its own
                      with nothing else. */}
                  {visitsReportButton}
                  </div>
                  {/* Date range */}
                  <div className="flex gap-2 items-center">
                    <Calendar className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                    <BRDateInput value={spDateFrom} onChange={setSpDateFrom} accentColor="orange" />
                    <span className="text-gray-600 text-xs">–</span>
                    <BRDateInput value={spDateTo} onChange={setSpDateTo} accentColor="orange" />
                    {(spDateFrom || spDateTo) && (
                      <button onClick={() => { setSpDateFrom(''); setSpDateTo(''); }} className="text-gray-500 hover:text-gray-300 transition-colors">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
              {isLoadingVisits ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
                </div>
              ) : filteredSpVisits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm">
                  <Briefcase className="h-8 w-8 text-gray-700 mb-2" />
                  <p>Nenhum prestador vinculado</p>
                </div>
              ) : (
                <div className="flex flex-col flex-1 min-h-0">
                <div className="space-y-2">
                  {paginatedSpVisits.map((sp) => {
                    const { label: stateLabel, color: stateColor } = getVisitStateLabel(sp.state);
                    const initials = sp.name.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('') || '?';

                    const SPAvatar = () => {
                      const [err, setErr] = React.useState(false);
                      if (!sp.photo || err) {
                        return (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-[11px] font-semibold text-white">{initials}</span>
                          </div>
                        );
                      }
                      return (
                        <img
                          src={getAbsoluteImageUrl(sp.photo)}
                          alt={sp.name}
                          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                          onError={() => setErr(true)}
                        />
                      );
                    };

                    return (
                      <div
                        key={sp.id}
                        onClick={() => onPersonClick?.(sp.personId)}
                        className={cn(
                          "bg-gray-950 rounded-lg p-3 border border-gray-800 hover:border-orange-900/50 transition-all",
                          onPersonClick && "cursor-pointer hover:bg-gray-900"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <SPAvatar />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span style={forceUpper ? { textTransform: 'uppercase' } : undefined} className="text-xs font-semibold text-gray-200 truncate">{sp.name}</span>
                              <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", stateColor)}>
                                {stateLabel}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              {sp.firstEntryAt && (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="h-3 w-3 flex-shrink-0" />
                                  Entrada: {formatDateTime(sp.firstEntryAt)}
                                </span>
                              )}
                              {sp.lastExitAt ? (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="h-3 w-3 flex-shrink-0" />
                                  Saída: {formatDateTime(sp.lastExitAt)}
                                </span>
                              ) : sp.state === 'revoked' ? (
                                <span className="text-xs text-red-400/70 flex items-center gap-1">
                                  <Clock className="h-3 w-3 flex-shrink-0" />
                                  Revogado: {formatDateTime(sp.revokedAt || sp.updatedAt)}
                                </span>
                              ) : (sp.state === 'exited' || sp.state === 'consumed') && sp.updatedAt ? (
                                <span className="text-xs text-gray-500/60 flex items-center gap-1">
                                  <Clock className="h-3 w-3 flex-shrink-0" />
                                  Saída manual: {formatDateTime(sp.updatedAt)}
                                </span>
                              ) : (sp.state === 'exited' || sp.state === 'consumed') ? (
                                <span className="text-xs text-gray-600 flex items-center gap-1">
                                  Saída não registrada
                                </span>
                              ) : !sp.firstEntryAt ? (
                                <span className="text-xs text-gray-600 flex items-center gap-1">
                                  <Calendar className="h-3 w-3 flex-shrink-0" />
                                  Cadastrado: {formatDateTime(sp.createdAt)}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {spTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-800 flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      {(spPage - 1) * PAGE_SIZE + 1}–{Math.min(spPage * PAGE_SIZE, filteredSpVisits.length)} de {filteredSpVisits.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSpPage(p => Math.max(1, p - 1))} disabled={spPage === 1}
                        className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 disabled:opacity-30 hover:bg-gray-700 transition-colors">
                        ‹ Anterior
                      </button>
                      <span className="text-xs text-gray-500 px-1">{spPage}/{spTotalPages}</span>
                      <button onClick={() => setSpPage(p => Math.min(spTotalPages, p + 1))} disabled={spPage === spTotalPages}
                        className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 disabled:opacity-30 hover:bg-gray-700 transition-colors">
                        Próximo ›
                      </button>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          ) : /* Encomendas tab content */
          locationType === 'unit' && activeTab === 'encomendas' ? (
            <div className="flex flex-col min-h-0">
              {isLoadingEncomendas ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
                </div>
              ) : encomendas.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm">
                  <Package className="h-8 w-8 text-gray-700 mb-2" />
                  <p>Nenhuma encomenda para esta unidade</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {encomendas.map(enc => (
                    <div
                      key={enc.id}
                      onClick={() => setSelectedEncomendaId(enc.id)}
                      className={cn(
                        'rounded-lg p-3 border transition-all cursor-pointer',
                        lightTheme
                          ? 'bg-white border-gray-200 hover:border-amber-300 hover:bg-amber-50/40'
                          : 'bg-gray-950 border-gray-800 hover:border-amber-800/50 hover:bg-gray-900',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {(() => {
                          // Tinted by STATUS: the tile is the first thing scanned down the list, and
                          // a retrieved parcel looked exactly like one still waiting at the gate.
                          const pending = enc.status === 'received' || enc.status === 'notified';
                          const tile = lightTheme
                            ? pending
                              ? 'bg-amber-100 text-amber-700'
                              : enc.status === 'returned'
                                ? 'bg-gray-100 text-gray-500'
                                : 'bg-emerald-100 text-emerald-700'
                            : pending
                              ? 'bg-amber-500/15 text-amber-300'
                              : enc.status === 'returned'
                                ? 'bg-gray-500/15 text-gray-400'
                                : 'bg-emerald-500/15 text-emerald-300';
                          return (
                            <div className={cn('p-2 rounded-lg flex-shrink-0', tile)}>
                              <Package className="h-4 w-4" />
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn(
                              'text-xs px-2 py-0.5 rounded border font-medium flex-shrink-0',
                              // This frame is light; the dark map's 400-on-10% was a pale smear here.
                              lightTheme ? ENCOMENDA_STATUS_COLORS_LIGHT[enc.status] : ENCOMENDA_STATUS_COLORS[enc.status],
                            )}>
                              {ENCOMENDA_STATUS_LABELS[enc.status]}
                            </span>
                            {enc.transportadora && (
                              <span className={cn("text-xs truncate font-medium", lightTheme ? "text-gray-800" : "text-gray-300")}>{enc.transportadora}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={cn("text-[10px] font-mono flex-shrink-0", lightTheme ? "text-gray-500" : "text-gray-600")}>#{enc.id.slice(0, 8).toUpperCase()}</span>
                            {enc.tracking_code && (
                              <span className={cn("text-xs font-mono truncate", lightTheme ? "text-gray-600" : "text-gray-500")}>{enc.tracking_code}</span>
                            )}
                            {enc.person && (
                              <span className={cn("text-xs truncate", lightTheme ? "text-gray-600" : "text-gray-500")}>
                                {enc.person.first_name} {enc.person.last_name}
                              </span>
                            )}
                            <span className={cn("text-xs ml-auto flex-shrink-0", lightTheme ? "text-gray-500" : "text-gray-600")}>
                              {formatDateTime(enc.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : /* Visit history tab content */
          (locationType === 'unit' || locationType === 'department') && activeTab === 'visits' ? (
            <div className="flex flex-col flex-1 min-h-0">
              {/* No separate "Sempre Habilitados" box: a permanent clearance IS a liberação and
                  is listed below with its own "Liberação permanente" label. The box also required
                  is_authorized, which a liberação only gets at the gate — so a permanent one was
                  in neither list. See unitLiberadosRows. */}
              {renderLiberados(liberadoVisits, 'Nenhum visitante liberado', isLoadingVisits)}
              {/* And who USED to be cleared for this unit — collapsed, so the live list keeps
                  the frame. */}
              {locationType === 'unit' && (
                <LiberadosHistoricoPanel
                  rows={historicoVisitantes}
                  isLoading={isLoadingHistorico}
                  onPersonClick={(personId) => onPersonClick?.(personId)}
                  lightTheme={lightTheme}
                  forceUpper={forceUpper}
                />
              )}

              {/* Filters */}
              {visits.length > 0 && (
                <div className="mb-3 flex-shrink-0 space-y-2">
                  <div className="flex gap-2">
                    {/* Text search */}
                    <div className="relative flex-1">
                      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Buscar por nome..."
                        value={visitPersonFilter}
                        onChange={e => setVisitPersonFilter(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 hover:border-gray-600 focus:border-blue-500 text-gray-200 text-xs rounded-lg pl-8 pr-7 py-2 focus:outline-none transition-colors placeholder-gray-600"
                      />
                      {visitPersonFilter && (
                        <button onClick={() => setVisitPersonFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                    {/* State select */}
                    <select
                      value={visitStateFilter}
                      onChange={e => setVisitStateFilter(e.target.value)}
                      className="bg-gray-900 border border-gray-700 hover:border-gray-600 focus:border-blue-500 text-gray-200 text-xs rounded-lg px-2.5 py-2 focus:outline-none transition-colors cursor-pointer"
                    >
                      <option value="">Todos</option>
                      <option value="inside">Dentro</option>
                      <option value="awaiting_entry">Aguardando</option>
                      <option value="pre_registered">Pré-Cadastrado</option>
                      <option value="exited">Saiu</option>
                      <option value="expired">Expirou</option>
                      <option value="revoked">Revogado</option>
                    </select>
                    {/* Relatório sits in the filter row rather than owning a line of its own —
                        a single button is not worth a row on a screen this dense. */}
                    {visitsReportButton}
                  </div>
                  {/* Date range */}
                  <div className="flex gap-2 items-center">
                    <Calendar className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                    <BRDateInput value={visitDateFrom} onChange={setVisitDateFrom} accentColor="blue" />
                    <span className="text-gray-600 text-xs">–</span>
                    <BRDateInput value={visitDateTo} onChange={setVisitDateTo} accentColor="blue" />
                    {(visitDateFrom || visitDateTo) && (
                      <button onClick={() => { setVisitDateFrom(''); setVisitDateTo(''); }} className="text-gray-500 hover:text-gray-300 transition-colors">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isLoadingVisits ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                </div>
              ) : filteredVisits.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm">
                  <History className="h-8 w-8 text-gray-700 mb-2" />
                  <p>Nenhuma visita registrada</p>
                </div>
              ) : (
                <div className="flex flex-col flex-1 min-h-0">
                <div className="space-y-2">
                  {paginatedVisits.map((visit) => {
                    const { label: stateLabel, color: stateColor } = getVisitStateLabel(visit.state);
                    const initials = visit.name.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('') || '?';

                    const VisitAvatar = () => {
                      const [err, setErr] = React.useState(false);
                      if (!visit.photo || err) {
                        return (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-[11px] font-semibold text-white">{initials}</span>
                          </div>
                        );
                      }
                      return (
                        <img
                          src={getAbsoluteImageUrl(visit.photo)}
                          alt={visit.name}
                          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                          onError={() => setErr(true)}
                        />
                      );
                    };

                    return (
                      <div
                        key={visit.id}
                        onClick={() => onPersonClick?.(visit.personId)}
                        className={cn(
                          "rounded-xl px-3 py-2.5 border transition-colors",
                          lightTheme
                            ? "bg-white border-gray-200 hover:border-gray-300"
                            : "bg-gray-950 border-gray-800 hover:border-gray-700",
                          onPersonClick && (lightTheme ? "cursor-pointer hover:bg-gray-50" : "cursor-pointer hover:bg-gray-900")
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <VisitAvatar />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                                className={cn("text-[13px] font-semibold truncate", lightTheme ? "text-gray-900" : "text-gray-200")}
                              >
                                {visit.name}
                              </span>
                              <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide", stateColor)}>
                                {stateLabel}
                              </span>
                            </div>
                            {/* ONE metadata line. This was three stacked lines of identically-sized
                                grey text, each repeating a clock icon — ~90px per visit, with
                                nothing in the hierarchy saying which part mattered. */}
                            <p className={cn("text-[11.5px] mt-0.5 truncate", lightTheme ? "text-gray-500" : "text-gray-500")}>
                              {[
                                visit.firstEntryAt ? `Entrada ${formatDateTime(visit.firstEntryAt)}` : null,
                                visit.lastExitAt ? `Saída ${formatDateTime(visit.lastExitAt)}` : null,
                                !visit.firstEntryAt && !visit.lastExitAt
                                  ? `Cadastrado ${formatDateTime(visit.createdAt)}`
                                  : null,
                                visit.authorizedByName ? `Aut. ${visit.authorizedByName}` : null,
                              ].filter(Boolean).join('  ·  ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {visitTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-800 flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      {(visitPage - 1) * PAGE_SIZE + 1}–{Math.min(visitPage * PAGE_SIZE, filteredVisits.length)} de {filteredVisits.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setVisitPage(p => Math.max(1, p - 1))}
                        disabled={visitPage === 1}
                        className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 disabled:opacity-30 hover:bg-gray-700 transition-colors"
                      >
                        ‹ Anterior
                      </button>
                      <span className="text-xs text-gray-500 px-1">{visitPage}/{visitTotalPages}</span>
                      <button
                        onClick={() => setVisitPage(p => Math.min(visitTotalPages, p + 1))}
                        disabled={visitPage === visitTotalPages}
                        className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 disabled:opacity-30 hover:bg-gray-700 transition-colors"
                      >
                        Próximo ›
                      </button>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {isLoadingList ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className={cn("h-6 w-6 animate-spin", locationType === 'block' ? "text-blue-500" : "text-blue-500")} />
                  </div>
                ) : locationType === 'block' ? (
                  // The lotes of this quadra, laid out like the quadra list they came from:
                  // a counted header, a search box that is always there, and a card that names
                  // what it is instead of a bare number in a box.
                  (() => {
                    const term = unitSearch.trim().toLowerCase();
                    const addressOf = (u: UnitInBlock) =>
                      u.street_name
                        ? (u.street_number ? `${u.street_name}, ${u.street_number}` : u.street_name)
                        : null;
                    const filtered = term
                      ? units.filter(u =>
                          (u.unit_number || u.name || '').toLowerCase().includes(term) ||
                          (addressOf(u) || '').toLowerCase().includes(term))
                      : units;
                    return (
                      <>
                        <div className="flex items-center gap-2 mb-2 flex-shrink-0">
                          <h3 className={cn("text-xs font-semibold uppercase tracking-wide flex-shrink-0", lightTheme ? "text-gray-700" : "text-gray-300")}>
                            {getUnitLabel(condoType, true)}
                          </h3>
                          <span
                            data-testid="unit-grid-count"
                            className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 flex-shrink-0"
                          >
                            {units.length}
                          </span>
                          <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
                            <input
                              type="text"
                              value={unitSearch}
                              onChange={e => setUnitSearch(e.target.value)}
                              placeholder={`Buscar ${unitLabel.toLowerCase()}...`}
                              className={cn(
                                "w-full rounded-lg pl-8 pr-3 py-1.5 text-xs border focus:outline-none",
                                lightTheme
                                  ? "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-blue-500"
                                  : "bg-gray-900 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-gray-500"
                              )}
                            />
                          </div>
                        </div>
                        {filtered.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {filtered.map((unit) => {
                              const address = addressOf(unit);
                              return (
                                <button
                                  key={unit.id}
                                  type="button"
                                  onClick={() => onUnitClick?.(unit.id, unit.name)}
                                  className={cn(
                                    "flex flex-col items-start gap-0.5 rounded-lg py-2.5 px-3 border transition-all text-left",
                                    lightTheme
                                      ? "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                      : "bg-gray-950 border-gray-800 hover:border-gray-700 hover:bg-gray-900"
                                  )}
                                >
                                  <span className={cn("text-sm font-semibold leading-tight truncate w-full", lightTheme ? "text-gray-900" : "text-gray-100")}>
                                    {unitLabel} {unit.unit_number || unit.name}
                                  </span>
                                  <span className="text-xs text-gray-500 truncate w-full leading-tight">
                                    {address || 'Clique para ver moradores'}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm">
                            <Home className="h-8 w-8 text-gray-700 mb-2" />
                            <p>{unitSearch ? `Nenhum ${unitLabel.toLowerCase()} encontrado` : `Nenhum ${unitLabel.toLowerCase()} cadastrado`}</p>
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : (
                  // Render people for departments and units
                  filteredPeople.length > 0 ? (
                    filteredPeople.slice((peoplePage - 1) * PEOPLE_PAGE_SIZE, peoplePage * PEOPLE_PAGE_SIZE).map((person) => {
                      const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Sem nome';
                      const initials = fullName.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');

                      // Try multiple possible photo field locations - different APIs return photos differently
                      // Cast to any for runtime fields that may exist but aren't in TypeScript type
                      const p = person as any;

                      // Primary fields to check (most common)
                      let photoUrl = p.identity_photo_url || p.profile_picture_url;

                      // Fallback: check nested structures
                      if (!photoUrl) {
                        photoUrl = p.identity_photo?.photo_url ||
                          p.identity_photos?.[0]?.photo_url ||
                          p.photo_url ||
                          p.photo ||
                          p.avatar_url ||
                          p.image_url ||
                          // Check nested person object (some APIs wrap data)
                          p.person?.identity_photo_url ||
                          p.person?.profile_picture_url ||
                          p.person?.identity_photo?.photo_url ||
                          // Check roles for photo (residents might have photos in role data)
                          p.roles?.find((r: any) => r.role_type === 'resident')?.photo_url ||
                          p.roles?.find((r: any) => r.role_type === 'resident')?.profile_picture_url;
                      }

                      // Avatar component with fallback
                      const PersonAvatar = () => {
                        const [imageError, setImageError] = React.useState(false);

                        if (!photoUrl || imageError) {
                          return (
                            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-[11px] font-semibold text-white">{initials}</span>
                            </div>
                          );
                        }

                        return (
                          <img
                            src={getAbsoluteImageUrl(photoUrl)}
                            alt={fullName}
                            className="h-7 w-7 rounded-full object-cover flex-shrink-0"
                            onError={() => setImageError(true)}
                          />
                        );
                      };

                      return (
                        <div
                          key={person.id}
                          onClick={() => onPersonClick?.(person.id)}
                          className={cn(
                            "rounded-lg px-2.5 py-1.5 border transition-all",
                            lightTheme
                              ? "bg-white border-gray-200 hover:border-gray-300"
                              : "bg-gray-950 border-gray-800 hover:border-gray-700",
                            onPersonClick && (lightTheme ? "cursor-pointer hover:bg-gray-50" : "cursor-pointer hover:bg-gray-900")
                          )}
                        >
                          {/* Name, vínculo, parentesco and phone on ONE line. Stacking the badges
                              under the name doubled the height of every resident for information
                              that fits beside it — on a portaria screen that is two visitors the
                              operator cannot see. */}
                          <div className="flex items-center gap-2 min-w-0">
                            <PersonAvatar />
                            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                              <h4 style={forceUpper ? { textTransform: 'uppercase' } : undefined} className={cn("text-sm font-semibold truncate", lightTheme ? "text-gray-900" : "text-gray-200")}>{fullName}</h4>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {(() => {
                                  // One vocabulary for both customer types (utils/relationType):
                                  // this map used to know only the residential words, so a
                                  // DEPARTMENT member's Responsável/Funcionário never appeared —
                                  // the drawer showed a name and nothing else where a unit shows
                                  // the position. The flat field wins when the endpoint projects
                                  // it; otherwise the membership for THIS location is read.
                                  const label = relationTypeLabel((person as any).relation_type)
                                    ?? personRelationLabel(person, locationType === 'department'
                                      ? { kind: 'staff', departmentId: locationId }
                                      : { kind: 'resident' });
                                  return label ? (
                                    <span className={cn(
                                      "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border",
                                      lightTheme ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-blue-900/40 text-blue-300 border-blue-700/30"
                                    )}>
                                      {label}
                                    </span>
                                  ) : null;
                                })()}
                                {(person as any).parentesco && (
                                  <span className={cn("text-xs", lightTheme ? "text-gray-500" : "text-gray-400")}>
                                    {getParentescoLabel((person as any).parentesco)}
                                  </span>
                                )}
                                {person.phone && (
                                  <span className={cn("text-xs whitespace-nowrap", lightTheme ? "text-gray-500" : "text-gray-400")}>{formatPhone(person.phone)}</span>
                                )}
                                {!person.phone && person.email && (
                                  <span className={cn("text-xs truncate max-w-[12rem]", lightTheme ? "text-gray-500" : "text-gray-400")}>{person.email}</span>
                                )}
                              </div>
                            </div>
                            {person.phone && (
                              <a
                                href={whatsappUrl(person.phone)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className={cn(
                                  "flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg border transition-colors",
                                  lightTheme
                                    ? "bg-green-100 hover:bg-green-200 border-green-300"
                                    : "bg-green-700/20 hover:bg-green-700/40 border-green-700/30"
                                )}
                                title="Abrir WhatsApp"
                              >
                                <svg viewBox="0 0 24 24" className={cn("w-3.5 h-3.5", lightTheme ? "fill-green-600" : "fill-green-400")}>
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                              </a>
                            )}
                            {(locationType === 'unit' && blockId || locationType === 'department') && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setAuthorizeVisitResident(person); }}
                                className={cn(
                                  "flex-shrink-0 flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-all",
                                  lightTheme
                                    ? "bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 border-blue-200 hover:border-blue-400"
                                    : "bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 hover:text-blue-300 border-blue-700/30 hover:border-blue-500/60"
                                )}
                                title="Autorizar Visita"
                              >
                                <UserPlus className="h-3.5 w-3.5" />
                                <span>Autorizar</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className={cn("flex flex-col items-center justify-center h-32 text-sm", lightTheme ? "text-gray-400" : "text-gray-500")}>
                      <User className={cn("h-8 w-8 mb-2", lightTheme ? "text-gray-300" : "text-gray-700")} />
                      <p>Nenhum {peopleLabel.toLowerCase()} cadastrado</p>
                    </div>
                  )
                )}
              </div>
              {filteredPeople.length > PEOPLE_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-2 flex-shrink-0">
                  <button
                    onClick={() => setPeoplePage(p => Math.max(1, p - 1))}
                    disabled={peoplePage === 1}
                    className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 disabled:opacity-30 hover:bg-gray-700 transition-colors"
                  >
                    ‹ Anterior
                  </button>
                  <span className="text-xs text-gray-500 px-1">{peoplePage}/{Math.ceil(filteredPeople.length / PEOPLE_PAGE_SIZE)}</span>
                  <button
                    onClick={() => setPeoplePage(p => Math.min(Math.ceil(filteredPeople.length / PEOPLE_PAGE_SIZE), p + 1))}
                    disabled={peoplePage === Math.ceil(filteredPeople.length / PEOPLE_PAGE_SIZE)}
                    className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 disabled:opacity-30 hover:bg-gray-700 transition-colors"
                  >
                    Próximo ›
                  </button>
                </div>
              )}

              {/* Vehicles section — for units and departments */}
              {(locationType === 'unit' || locationType === 'department') && (
                <div className="mt-4 flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold flex items-center gap-2 text-gray-300 uppercase tracking-wide">
                      {locationType === 'department' ? 'VEÍCULOS DO DEPARTAMENTO' : 'VEÍCULOS DA UNIDADE'}
                      <span className="text-xs font-medium text-gray-500">
                        {vehicles.length}
                      </span>
                    </h3>
                    <button
                      onClick={() => setShowVehicleForm(true)}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                      title="Cadastrar veículo para esta unidade"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Veículo</span>
                    </button>
                  </div>

                  {isLoadingVehicles ? (
                    <div className="flex items-center justify-center h-16">
                      <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                    </div>
                  ) : vehicles.length > 0 ? (
                    <div className="space-y-1.5">
                      {/* One line per vehicle. This list IS the unit's vehicles, so repeating
                          "Bloco A · Unidade 14" under every plate said nothing and cost two rows of
                          height each. The owner's name does earn its place — it is the one thing
                          that differs between cars of the same unit. */}
                      {vehicles.map((v: any) => (
                        <div key={v.id} onClick={() => setSelectedVehicle(v)} className="flex items-center gap-2 bg-gray-950 rounded-lg px-2.5 py-1.5 border border-gray-800 cursor-pointer hover:bg-gray-900 hover:border-blue-700/50 transition-all">
                          <LicensePlate plate={v.plate} size="sm" />
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            {(v.brand || v.model || v.color) && (
                              <span className="text-xs text-gray-400 truncate">
                                {[v.brand, v.model, v.color].filter(Boolean).join(' · ')}
                              </span>
                            )}
                            {v.person && (
                              <span
                                style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                                className="text-xs text-blue-400 truncate"
                                title={`${v.person.first_name} ${v.person.last_name}`}
                              >
                                {v.person.first_name} {v.person.last_name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleVehicleSyncRevoke(v, true); }}
                              disabled={syncingVehicleId === v.id}
                              className="p-1.5 rounded hover:bg-blue-500/10 text-gray-500 hover:text-blue-400 transition-colors disabled:opacity-40"
                              title="Sincronizar"
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${syncingVehicleId === v.id ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleVehicleSyncRevoke(v, false); }}
                              disabled={revokingVehicleId === v.id}
                              className="p-1.5 rounded hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors disabled:opacity-40"
                              title="Revogar acesso"
                            >
                              <ShieldOff className={`h-3.5 w-3.5 ${revokingVehicleId === v.id ? 'animate-pulse' : ''}`} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-14 text-gray-600 text-xs border border-dashed border-gray-800 rounded-lg">
                      {locationType === 'department' ? 'Nenhum veículo cadastrado para este departamento' : 'Nenhum veículo cadastrado para esta unidade'}
                    </div>
                  )}
                </div>
              )}

              {/* Pets section — read-only, only for units with feature enabled */}
              {locationType === 'unit' && petsEnabled && (
                <div className="mt-4 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">PETS DA UNIDADE</h3>
                    <span className="text-xs font-medium text-gray-500">
                      {pets.length}
                    </span>
                  </div>

                  {pets.length > 0 ? (
                    <div className="space-y-1.5">
                      {pets.map((pet) => (
                        <button
                          key={pet.id}
                          onClick={() => setSelectedPet(pet)}
                          className="w-full flex items-center gap-3 bg-gray-950 rounded-lg px-3 py-2 border border-gray-800 hover:border-amber-700/50 hover:bg-gray-900 transition-all text-left"
                        >
                          {pet.photo_url ? (
                            <img src={getAbsoluteImageUrl(pet.photo_url)} alt={pet.name} className="h-9 w-9 rounded-full object-cover flex-shrink-0 ring-1 ring-amber-700/40" />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                              <PawPrint className="h-4 w-4 text-amber-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-200 truncate">{pet.name}</span>
                            {/* Same espécie · [raça] as the Animais list — this strip showed the
                                breed alone, so "Siamês" never said cat. */}
                            {petSpeciesLabel(pet.species) && (
                              <span className="text-xs text-gray-500 flex-shrink-0">{petSpeciesLabel(pet.species)}</span>
                            )}
                            {petBreedLabel(pet.breed) && (
                              <span
                                title={petBreedLabel(pet.breed)}
                                className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 truncate"
                              >
                                {petBreedLabel(pet.breed)}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-14 text-gray-600 text-xs border border-dashed border-gray-800 rounded-lg">
                      Nenhum pet cadastrado para esta unidade
                    </div>
                  )}
                </div>
              )}

              {/* Funcionários section — read-only, only for units with feature enabled */}
              {locationType === 'unit' && funcionariosEnabled && (
                <div className="mt-4 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">FUNCIONÁRIOS DA UNIDADE</h3>
                    <span className="text-xs font-medium text-gray-500">
                      {unitFuncionarios.length}
                    </span>
                  </div>

                  {unitFuncionarios.length > 0 ? (
                    <div className="space-y-1.5">
                      {unitFuncionarios.map((employee) => {
                        const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Sem nome';
                        const photoUrl = getAbsoluteImageUrl(employee.identity_photo_url || employee.profile_picture_url);
                        return (
                          <button
                            key={employee.id}
                            onClick={() => onPersonClick?.(employee.id)}
                            className={cn(
                              "w-full flex items-center gap-3 bg-gray-950 rounded-lg px-3 py-2 border border-gray-800 hover:border-blue-700/50 hover:bg-gray-900 transition-all text-left",
                              onPersonClick && "cursor-pointer"
                            )}
                          >
                            {photoUrl ? (
                              <img src={photoUrl} alt={fullName} className="h-9 w-9 rounded-full object-cover flex-shrink-0 ring-1 ring-blue-700/40" />
                            ) : (
                              <div className="h-9 w-9 rounded-full bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                <Briefcase className="h-4 w-4 text-blue-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-200 truncate">{fullName}</span>
                              {employee.phone && (
                                <span className="ml-2 text-xs text-gray-500">{employee.phone}</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-14 text-gray-600 text-xs border border-dashed border-gray-800 rounded-lg">
                      Nenhum funcionário cadastrado para esta unidade
                    </div>
                  )}
                </div>
              )}

              {/* Documents section — units only */}
              {locationType === 'unit' && (
                <div className="mt-4 flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">DOCUMENTOS</h3>
                      <span className="text-xs font-medium text-gray-500">
                        {unitDocuments.length}
                      </span>
                    </div>
                    <label className={`flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-700/40 rounded-lg transition-colors cursor-pointer ${uploadingDoc ? 'opacity-50 pointer-events-none' : ''}`}>
                      {uploadingDoc ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {uploadingDoc ? 'Enviando...' : 'Enviar'}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        disabled={uploadingDoc}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 3 * 1024 * 1024) {
                            toast.error('Arquivo excede o limite de 3MB');
                            e.target.value = '';
                            return;
                          }
                          setUploadingDoc(true);
                          try {
                            await condoApi.uploadUnitDocument(customerId, locationId, file);
                            const data = await condoApi.listUnitDocuments(customerId, locationId);
                            setUnitDocuments(data?.documents ?? []);
                            toast.success('Documento enviado!');
                          } catch (err: any) {
                            toast.error(err?.response?.data?.error || 'Erro ao enviar documento');
                          } finally {
                            setUploadingDoc(false);
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                  </div>

                  {unitDocuments.length === 0 ? (
                    <div className="flex items-center justify-center h-14 text-gray-600 text-xs border border-dashed border-gray-800 rounded-lg">
                      Nenhum documento cadastrado
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {unitDocuments.map((doc) => {
                        const isPdf = doc.file_type === 'application/pdf';
                        const sizeKb = (doc.file_size / 1024).toFixed(0);
                        return (
                          <div key={doc.id} className="flex items-center gap-2 bg-gray-950 rounded-lg px-3 py-2 border border-gray-800">
                            <div className="h-8 w-8 rounded-lg bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              {isPdf
                                ? <FileText className="h-3.5 w-3.5 text-blue-400" />
                                : <FileImage className="h-3.5 w-3.5 text-blue-400" />
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-200 truncate">{doc.name}</p>
                              <p className="text-[10px] text-gray-500">{sizeKb} KB · {isPdf ? 'PDF' : 'Imagem'}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-gray-500 hover:text-blue-400 transition-colors"
                                title="Abrir"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                              <button
                                onClick={async () => {
                                  try {
                                    await condoApi.deleteUnitDocument(customerId, locationId, doc.id);
                                    setUnitDocuments(prev => prev.filter(d => d.id !== doc.id));
                                    toast.success('Documento removido');
                                  } catch {
                                    toast.error('Erro ao remover documento');
                                  }
                                }}
                                className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                                title="Remover"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Right column - Info card */}
        {!hideSideInfo && (
        <div className="flex flex-col min-h-0">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">
            Informacoes
          </h3>

          {isLoadingInfo ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 text-gray-500 animate-spin" />
            </div>
          ) : (
            <div className="bg-gray-950 rounded-lg border border-gray-800 p-4 space-y-4">
              {/* Count - Units for blocks, People for others */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  {locationType === 'block' ? (
                    <Home className="h-4 w-4" />
                  ) : (
                    <Users className="h-4 w-4" />
                  )}
                  {locationType === 'block' ? `Total de ${unitLabel.toLowerCase()}s` : `Total de ${peopleLabel.toLowerCase()}`}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-200">
                    {locationType === 'block' ? units.length : people.length}
                  </span>
                  {locationType === 'block' && (
                    <button
                      type="button"
                      onClick={() => setShowUnitForm(true)}
                      title={`${getUnitArticle(condoType).ordinal} ${unitLabel}`}
                      className="p-1 rounded-md bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Description (for departments and blocks) */}
              {locationInfo?.description && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Descricao</span>
                  <p className="text-sm text-gray-300">{locationInfo.description}</p>
                </div>
              )}

              {/* Manager (for departments) */}
              {locationInfo?.manager_name && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Responsavel
                  </span>
                  <span className="text-sm text-gray-300">{locationInfo.manager_name}</span>
                </div>
              )}

              {/* Bloco and endereço used to be here too — they only ever applied to a unit, whose
                  card this no longer is. They live in the header tooltip now. */}

              {/* Creation date */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Criado em
                </span>
                <span className="text-sm text-gray-300">{formatDate(locationInfo?.created_at)}</span>
              </div>

              {/* Last update */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Atualizado em
                </span>
                <span className="text-sm text-gray-300">{formatDate(locationInfo?.updated_at)}</span>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Liberar acesso — the portaria's counterpart to the morador app's liberação flow. */}
      {locationType === 'unit' && (
        <LiberarAcessoModal
          open={showLiberarModal}
          customerId={customerId}
          unitId={locationId}
          unitDisplay={locationInfo?.block_name ? `${locationInfo.block_name} ${locationInfo?.name || ''}`.trim() : locationInfo?.name}
          unitNumber={locationInfo?.name}
          blockId={blockId}
          blockName={locationInfo?.block_name}
          onClose={() => setShowLiberarModal(false)}
          onCreated={() => { setShowLiberarModal(false); setRefreshTrigger(prev => prev + 1); }}
          lightTheme={lightTheme}
        />
      )}

      {/* Pet detail modal */}
      {selectedPet && (
        <PetDetailModal
          pet={selectedPet}
          customerId={customerId}
          condoType={condoType}
          onClose={() => setSelectedPet(null)}
          onPetUpdated={() => setRefreshTrigger(prev => prev + 1)}
          onPetDeleted={() => { setSelectedPet(null); setRefreshTrigger(prev => prev + 1); }}
          lightTheme={lightTheme}
        />
      )}

      {/* Vehicle detail modal */}
      <VehicleDetailModal
        open={!!selectedVehicle}
        vehicle={selectedVehicle}
        customerId={customerId}
        customerType={customerType}
        onClose={() => setSelectedVehicle(null)}
        onEdit={(v) => { setSelectedVehicle(null); setVehicleToEdit(v); setShowVehicleForm(true); }}
        onSync={(v) => handleVehicleSyncRevoke(v, true)}
        isSyncing={!!(selectedVehicle && syncingVehicleId === selectedVehicle.id)}
        onRevoke={(v) => handleVehicleSyncRevoke(v, false)}
        isRevoking={!!(selectedVehicle && revokingVehicleId === selectedVehicle.id)}
        onDelete={(v) => { setSelectedVehicle(null); handleDeleteVehicle(v); }}
        onPersonClick={onPersonClick}
        onUnitClick={onUnitClick ? (unitId) => { setSelectedVehicle(null); onUnitClick(unitId); } : undefined}
        lightTheme={lightTheme}
      />

      {/* Unit/lote creation modal — block always pre-selected, opened from this block's own panel */}
      {locationType === 'block' && showUnitForm && (
        <UnitFormModal
          preselectedBlockId={locationId}
          preselectedBlockName={locationInfo?.name || locationLabel}
          blockFloors={locationInfo?.floors}
          condoType={condoType}
          existingNumbers={units.map(u => u.unit_number || u.name)}
          onClose={() => setShowUnitForm(false)}
          onCreated={() => setRefreshTrigger(prev => prev + 1)}
        />
      )}

      {/* Vehicle form modal — unit-level vehicle registration */}
      {locationType === 'unit' && (
        <VehicleFormModal
          open={showVehicleForm}
          editingVehicle={vehicleToEdit}
          onClose={() => { setShowVehicleForm(false); setVehicleToEdit(null); }}
          onSuccess={() => {
            setShowVehicleForm(false);
            setVehicleToEdit(null);
            setRefreshTrigger(prev => prev + 1);
          }}
          customerId={customerId}
          customerType={customerType}
          condoType={condoType}
          preselectedUnitId={vehicleToEdit ? undefined : locationId}
          preselectedUnitName={vehicleToEdit ? undefined : locationInfo?.name}
          preselectedOwnerType={vehicleToEdit ? undefined : "resident"}
          darkMode={!lightTheme}
        />
      )}

      {/* Encomenda modals */}
      {locationType === 'unit' && (
        <>
          <EncomendaDetailModal
            open={!!selectedEncomendaId}
            encomendaId={selectedEncomendaId}
            customerId={customerId}
            onClose={() => setSelectedEncomendaId(null)}
            onStatusChange={() => setRefreshTrigger(prev => prev + 1)}
          />
          <EncomendaFormModal
            open={showEncomendaForm}
            customerId={customerId}
            preselectedUnitId={locationId}
            preselectedUnitName={locationInfo?.name}
            onClose={() => setShowEncomendaForm(false)}
            onSuccess={() => {
              setShowEncomendaForm(false);
              setRefreshTrigger(prev => prev + 1);
            }}
          />
        </>
      )}
      <VisitorRegistrationModal
        open={!!authorizeVisitResident}
        onClose={() => setAuthorizeVisitResident(null)}
        customerId={customerId}
        selectedCustomer={{ type: customerType, mobileAppEnabled }}
        initialBlockId={locationType === 'unit' ? (blockId || undefined) : undefined}
        initialUnitId={locationType === 'unit' ? locationId : undefined}
        initialDepartmentId={locationType === 'department' ? locationId : undefined}
        initialResidentId={authorizeVisitResident?.id}
        onSuccess={() => {
          setAuthorizeVisitResident(null);
          setRefreshTrigger(prev => prev + 1);
        }}
        darkMode={!lightTheme}
      />
      {/* Department — Nova Visita */}
      <VisitorRegistrationModal
        open={showDeptNewVisitModal}
        onClose={() => setShowDeptNewVisitModal(false)}
        customerId={customerId}
        selectedCustomer={{ type: customerType, mobileAppEnabled }}
        initialStep="form"
        initialDepartmentId={locationType === 'department' ? locationId : undefined}
        onSuccess={() => { setShowDeptNewVisitModal(false); setRefreshTrigger(prev => prev + 1); }}
        darkMode={!lightTheme}
      />
      {/* Department — Autorizar Visita */}
      <VisitorRegistrationModal
        open={showDeptAuthorizeModal}
        onClose={() => setShowDeptAuthorizeModal(false)}
        customerId={customerId}
        selectedCustomer={{ type: customerType, mobileAppEnabled }}
        initialStep="search"
        initialDepartmentId={locationType === 'department' ? locationId : undefined}
        onSuccess={() => { setShowDeptAuthorizeModal(false); setRefreshTrigger(prev => prev + 1); }}
        darkMode={!lightTheme}
      />
      {/* Department — Novo Funcionário */}
      <EmployeeRegistrationModal
        isOpen={showDeptEmployeeModal}
        onClose={() => setShowDeptEmployeeModal(false)}
        onSubmit={handleCreateEmployee}
        departments={allDepts.length > 0 ? allDepts : (locationInfo ? [{ id: locationId, name: locationInfo.name }] : [])}
        isLoading={isSubmittingEmployee}
        customerId={customerId}
        defaultDepartmentId={locationId}
        darkMode={!lightTheme}
      />
    </div>
  );
};
